import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import EditJobModal from "@/components/EditJobModal";
import SendOfferModal from "@/components/SendOfferModal";
import JobDetailsModal from "@/components/JobDetailsModal";
import AdvancedJobDetailsModal from "@/components/AdvancedJobDetailsModal";
import AdvancedEditJobModal from "@/components/AdvancedEditJobModal";
import ApplicantsModal from "@/components/ApplicantsModal";
import ProfileBlockerModal from "@/components/ProfileBlockerModal";
import CancellationPaymentBlockerModal from "@/components/CancellationPaymentBlockerModal";
import CompleteJobModal from "@/components/CompleteJobModal";
import ReviewModal from "@/components/ReviewModal";
import CancelCrewMemberModal from "@/components/CancelCrewMemberModal";
import CancelUnhiredPositionModal from "@/components/CancelUnhiredPositionModal";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import {
  Calendar,
  Users,
  DollarSign,
  Plus,
  Eye,
  X,
  MapPin,
  Briefcase,
  Clock,
  MessageSquare,
  CheckCircle,
  Search,
  TrendingUp,
  ChevronRight,
  ChevronLeft,
  UserCheck,
  AlertCircle,
  Award,
  Phone,
  Mail,
  Heart,
  Lock,
  Edit,
  User,
  Camera,
  Link,
  Globe,
  ExternalLink,
  Image,
  Star,
  Shield,
  Send,
  Share2,
  Check as CheckIcon
} from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import linkifyText from "@/utils/linkifyText";
import { loadGoogleMapsScript } from '@/utils/googleMapsLoader';
import "@/styles/commandDeck.css";

// Helper to get role-specific badge colors and format role name
const getRoleBadgeStyle = (role) => {
  const roleLower = role?.toLowerCase() || '';
  const capitalizedRole = role?.replace(/_/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || 'Unknown';

  if (roleLower.includes('photographer') || roleLower.includes('photo')) {
    return {
      className: 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white',
      displayRole: capitalizedRole
    };
  }
  if (roleLower.includes('videographer') || roleLower.includes('video')) {
    return {
      className: 'bg-gradient-to-r from-purple-500 to-pink-500 text-white',
      displayRole: capitalizedRole
    };
  }
  if (roleLower.includes('editor')) {
    return {
      className: 'bg-gradient-to-r from-orange-500 to-amber-500 text-white',
      displayRole: capitalizedRole
    };
  }
  if (roleLower.includes('drone') || roleLower.includes('aerial')) {
    return {
      className: 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white',
      displayRole: capitalizedRole
    };
  }
  if (roleLower.includes('assistant') || roleLower.includes('second')) {
    return {
      className: 'bg-gradient-to-r from-rose-500 to-red-500 text-white',
      displayRole: capitalizedRole
    };
  }
  // Default for other roles
  return {
    className: 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white',
    displayRole: capitalizedRole
  };
};

// Normalize a role string for comparison: lowercase, spaces → underscores
const normalizeRole = (role) => (role || '').toLowerCase().replace(/\s+/g, '_').trim();

// Does the applicant's role match any role in the given Set of normalized role strings?
// Exact equality only — substring matching here cross-matched canonical roles whose
// normalized strings overlap (e.g. "voxpop_videographer" ⊃ "videographer").
const applicantRoleMatchesAny = (applicantRole, roleSet) => {
  if (!applicantRole || !roleSet || roleSet.size === 0) return false;
  return roleSet.has(applicantRole);
};

// Helper to check if an applicant can receive an offer.
// pendingOfferRoles: Set of normalized role strings that currently have a pending offer
// pendingOfferApplicantIds: Set of applicant user_ids that currently have a pending offer
const canSendOfferToApplicant = (job, applicant, pendingOfferRoles = new Set(), pendingOfferApplicantIds = new Set()) => {
  const applicantRole = normalizeRole(applicant.role);

  // For advanced jobs, check if this applicant's role is already filled or cancelled
  if (job.isAdvanced && job.crewMembers && job.crewMembers.length > 0) {
    const filledRoleSet = new Set(
      job.crewMembers.filter(cm => cm.hired).map(cm => normalizeRole(cm.role))
    );
    if (applicantRoleMatchesAny(applicantRole, filledRoleSet)) {
      return false; // This role is already filled
    }
    const cancelledRoleSet = new Set(
      job.crewMembers.filter(cm => cm.cancelled).map(cm => normalizeRole(cm.role))
    );
    if (applicantRoleMatchesAny(applicantRole, cancelledRoleSet)) {
      return false; // This role has been cancelled
    }
  }

  // Advanced jobs: scope pending-offer check by role
  if (job.isAdvanced) {
    // No applicant role known — fall back to allowing
    if (!applicantRole) return true;
    if (applicantRoleMatchesAny(applicantRole, pendingOfferRoles)) return false;
    // Legacy fallback: if backend hasn't populated pendingOfferRoles yet but
    // job.pending_offer exists with a role, use it
    if (job.pending_offer?.role) {
      const legacyRole = normalizeRole(job.pending_offer.role);
      if (applicantRoleMatchesAny(applicantRole, new Set([legacyRole]))) return false;
    }
    return true;
  }

  // Simple (non-advanced) jobs: any pending offer or pending applicant blocks
  if (job.pending_offer) return false;
  if (pendingOfferApplicantIds.size > 0) return false;
  return true;
};

const JOBS_PAGE_SIZE = 15;

const HireCrewRedesigned = ({ onLogout }) => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = JSON.parse(localStorage.getItem('shootzu_user') || '{}');
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [showEditJobModal, setShowEditJobModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'archived'
  const [postedJobs, setPostedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobsPage, setJobsPage] = useState(1);
  const [hasMoreJobs, setHasMoreJobs] = useState(true);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [tabCounts, setTabCounts] = useState({ active: 0, archived: 0 });
  const jobsRequestId = useRef(0);
  const [jobApplicants, setJobApplicants] = useState([]);
  const [loadingApplicants, setLoadingApplicants] = useState(false);
  const [jobOffers, setJobOffers] = useState([]);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [fullApplicantProfile, setFullApplicantProfile] = useState(null);
  const [loadingFullProfile, setLoadingFullProfile] = useState(false);
  const [detailsTab, setDetailsTab] = useState('overview'); // 'overview' or 'applicants'
  const [showSendOfferModal, setShowSendOfferModal] = useState(false);
  const [offerApplicant, setOfferApplicant] = useState(null);
  const [mobileTab, setMobileTab] = useState('details'); // 'details' or 'applicants' for mobile view
  const [showProfileBlocker, setShowProfileBlocker] = useState(false);
  const [showCompleteJobModal, setShowCompleteJobModal] = useState(false);
  const [userStatus, setUserStatus] = useState('');
  const [outstandingCancellationFees, setOutstandingCancellationFees] = useState(null);
  const [showCancellationBlocker, setShowCancellationBlocker] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewingCrewMember, setReviewingCrewMember] = useState(null); // For per-crew-member review in advanced jobs
  const [cancellingCrewMember, setCancellingCrewMember] = useState(null);
  const [cancellingCrewMemberIndex, setCancellingCrewMemberIndex] = useState(null);
  const [showCancelCrewMemberModal, setShowCancelCrewMemberModal] = useState(false);
  const [cancellingPosition, setCancellingPosition] = useState(null);
  const [cancellingPositionIndex, setCancellingPositionIndex] = useState(null);
  const [showCancelPositionModal, setShowCancelPositionModal] = useState(false);

  // Portfolio lightbox state
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [lightboxImages, setLightboxImages] = useState([]);

  // Share state
  const [copiedJobId, setCopiedJobId] = useState(null);

  // Fetch user status from database on mount
  useEffect(() => {
    const fetchUserStatus = async () => {
      if (user.id) {
        try {
          const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/profile/${user.id}`);
          const dbStatus = (response.data.status || '').toUpperCase();
          console.log('📊 HireCrew - User status from DB:', dbStatus);
          setUserStatus(dbStatus);
        } catch (error) {
          console.error('Error fetching user status:', error);
        }
      }
      if (user.email) {
        try {
          const feesResponse = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/user/outstanding-cancellation-fees`,
            { params: { user_email: user.email } }
          );
          if (feesResponse.data.has_outstanding_fees) {
            setOutstandingCancellationFees(feesResponse.data);
            setShowCancellationBlocker(true);
          }
        } catch (err) {
          console.error('Failed to check cancellation fees:', err);
        }
      }
    };
    fetchUserStatus();
  }, [user.id]);
  
  // Handle email link actions (when user clicks email buttons) or notification clicks (openJob)
  useEffect(() => {
    const action = searchParams.get('action');
    const job_id = searchParams.get('job_id');
    const openJobId = searchParams.get('openJob');

    // Handle openJob from notification click - open job details modal with applicants
    if (openJobId && postedJobs.length > 0) {
      setTimeout(() => {
        const job = postedJobs.find(j => j.id === openJobId);
        if (job) {
          setSelectedJob(job);
          setShowApplicants(true);  // Show applicants view for new job applications
          // Clean URL after action
          navigate('/hire-crew', { replace: true });
        }
      }, 500);
      return; // Don't process other actions
    }

    if (action && job_id) {
      setTimeout(() => {
        const job = postedJobs.find(j => j.id === job_id);
        if (job) {
          if (action === 'view_applicants') {
            setSelectedJob(job);
            setShowApplicants(true);
          } else if (action === 'view_details') {
            setSelectedJob(job);
            setShowJobDetails(true);
          }

          // Clean URL after action
          navigate('/hire-crew', { replace: true });
        }
      }, 1000); // Wait for jobs to load
    }
  }, [searchParams, postedJobs]);
  
  const [jobForm, setJobForm] = useState({
    title: '',
    dates: [''],
    location: '',
    type: 'photographer',
    brief: '',
    ratePerHour: '',
    hours: ''
  });

  // Shortlist helper functions (saves to database)
  const toggleShortlist = async (jobId, userId) => {
    try {
      const job = postedJobs.find(j => j.id === jobId) || selectedJob;
      if (!job) return;
      
      const shortlisted = job.shortlisted_users || [];
      const isCurrentlyShortlisted = shortlisted.includes(userId);
      
      // Optimistically update UI immediately
      const newShortlisted = isCurrentlyShortlisted 
        ? shortlisted.filter(id => id !== userId)
        : [...shortlisted, userId];
      
      if (selectedJob && selectedJob.id === jobId) {
        setSelectedJob(prev => ({ ...prev, shortlisted_users: newShortlisted }));
      }
      setPostedJobs(prev => prev.map(j => 
        j.id === jobId ? { ...j, shortlisted_users: newShortlisted } : j
      ));

      if (isCurrentlyShortlisted) {
        // Remove from shortlist
        await axios.delete(
          `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${jobId}/shortlist/${userId}`,
          { params: { business_owner_email: user.email } }
        );
        toast.success('Removed from shortlist');
      } else {
        // Add to shortlist
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${jobId}/shortlist/${userId}`,
          null,
          { params: { business_owner_email: user.email } }
        );
        toast.success('Added to shortlist');
      }
      
      
    } catch (error) {
      console.error('Error toggling shortlist:', error);
      toast.error('Failed to update shortlist');
    }
  };

  // Share job link function
  const handleShareJob = async (jobId, e) => {
    if (e) e.stopPropagation();

    const shareUrl = `${window.location.origin}/jobs/${jobId}`;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedJobId(jobId);
      toast.success('Job link copied to clipboard!');
      setTimeout(() => setCopiedJobId(null), 3000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedJobId(jobId);
      toast.success('Job link copied to clipboard!');
      setTimeout(() => setCopiedJobId(null), 3000);
    }
  };

  useEffect(() => {
    // Load Google Maps for job location
    loadGoogleMapsScript(() => {
      console.log('Google Maps loaded for HireCrew');
    });
  }, []);

  // Lock body scroll when modal is open - iOS Safe method
  useEffect(() => {
    if (showJobDetails) {
      // iOS-safe scroll lock: Don't use position:fixed
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [showJobDetails]);

  // Search runs server-side (across ALL the user's jobs), so debounce keystrokes
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Tab + search are filtered server-side so each page holds JOBS_PAGE_SIZE visible jobs
  useEffect(() => {
    fetchPostedJobs(1);
  }, [activeTab, debouncedSearch]);

  const fetchPostedJobs = async (page = 1) => {
    const requestId = ++jobsRequestId.current;
    try {
      setLoading(true);
      const params = new URLSearchParams({
        user_email: user.email || 'test@shootzu.com',
        page: String(page),
        limit: String(JOBS_PAGE_SIZE),
        tab: activeTab,
      });
      if (debouncedSearch) params.set('search', debouncedSearch);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/jobs/my-posted-jobs?${params.toString()}`
      );
      // Ignore stale responses from an earlier tab/search
      if (requestId !== jobsRequestId.current) return;
      if (response.data.tab_counts) setTabCounts(response.data.tab_counts);

      const rawJobs = response.data.jobs || [];
      
      const mappedJobs = rawJobs.map(job => {
        // Calculate hours and rate for advanced vs simple jobs
        let totalHours = job.hours || 0;
        let avgRate = job.pay_rate || 0;

        // For advanced jobs, calculate from crew members
        if ((job.crew_count && job.crew_count > 1) || (job.crew_members && job.crew_members.length > 1)) {
          totalHours = job.crew_members ? job.crew_members.reduce((sum, member) => sum + (member.hours || 0), 0) : 0;
          const totalCost = job.crew_members ? job.crew_members.reduce((sum, member) => sum + ((member.hours || 0) * (member.ratePerHour || 0)), 0) : 0;
          avgRate = totalHours > 0 ? totalCost / totalHours : 0;
        } else if (!avgRate && job.crew_members && job.crew_members.length === 1) {
          // Simple jobs store pricing in crew_members[0]
          avgRate = job.crew_members[0].ratePerHour || 0;
          totalHours = totalHours || job.crew_members[0].hours || 0;
        }
        
        return {
          id: job.id,
          jobNumber: job.job_number,
          title: job.title,
          description: job.description || '',
          jobDate: job.date,
          ratePerHour: avgRate,
          hours: totalHours,
          total: job.budget || job.total_amount || (avgRate * totalHours) || 0,
          location: job.location_address || '',
          type: job.job_type || 'photographer',
          requiredRoles: job.required_roles || [],
          status: job.status,
          applicants: job.application_count || 0,
          // Offer tracking
          offer_sent: job.offer_sent || false,
          pending_offer: job.pending_offer || null,
          // Advanced job data
          dates: job.dates || [job.date],
          crewCount: job.crew_count || 1,
          crewMembers: job.crew_members || [],
          isAdvanced: (job.dates && job.dates.length > 1) || (job.crew_count && job.crew_count > 1) || (job.crew_members && job.crew_members.length > 1),
          // For advanced jobs, extract hired crew from crew_members array
          hiredCrewMembers: (job.crew_members || []).map((cm, origIdx) => ({ ...cm, originalIndex: origIdx })).filter(cm => cm.hired).map(cm => ({
            userId: cm.hired_user_id,
            userNumber: cm.hired_user_number,
            name: cm.hired_name || 'Unknown',
            email: cm.hired_email,
            phone: cm.hired_phone,
            photo: cm.hired_photo || 'https://via.placeholder.com/150',
            role: cm.role || 'Crew Member',
            hours: cm.hours || 0,
            ratePerHour: cm.ratePerHour || 0,
            subtotal: cm.subtotal || 0,
            crewPayout: cm.crew_payout || (cm.subtotal ? Math.round(cm.subtotal * 0.98 * 100) / 100 : 0),
            paymentReleased: cm.payment_released || false,
            paymentReleasedAt: cm.payment_released_at || null,
            reviewSubmitted: cm.review_submitted || false,
            originalIndex: cm.originalIndex
          })),
          // For simple jobs, use assigned_crew; for advanced jobs with only 1 hired, also populate hiredCrew for backwards compat
          hiredCrew: job.assigned_crew ? {
            userId: job.assigned_crew.user_id,
            userNumber: job.assigned_crew.user_number,
            name: job.assigned_crew.name || 'Unknown',
            email: job.assigned_crew.email,
            phone: job.assigned_crew.phone,
            photo: job.assigned_crew.photo || 'https://via.placeholder.com/150',
            role: job.assigned_crew.role || 'Crew Member'
          } : ((job.crew_members || []).filter(cm => cm.hired).length === 1 ? {
            userId: (job.crew_members || []).find(cm => cm.hired)?.hired_user_id,
            name: (job.crew_members || []).find(cm => cm.hired)?.hired_name || 'Unknown',
            email: (job.crew_members || []).find(cm => cm.hired)?.hired_email,
            phone: (job.crew_members || []).find(cm => cm.hired)?.hired_phone,
            photo: (job.crew_members || []).find(cm => cm.hired)?.hired_photo || 'https://via.placeholder.com/150',
            role: (job.crew_members || []).find(cm => cm.hired)?.role || 'Crew Member'
          } : null),
          // Per-role applicant counts for advanced jobs
          applicantsByRole: job.applicantsByRole || {}
        };
      });
      
      if (page === 1) {
        setPostedJobs(mappedJobs);
      } else {
        setPostedJobs(prev => [...prev, ...mappedJobs]);
      }
      
      setJobsPage(page);
      setHasMoreJobs(page * JOBS_PAGE_SIZE < (response.data.total || 0));
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load your posted jobs');
      if (page === 1 && requestId === jobsRequestId.current) setPostedJobs([]);
    } finally {
      if (requestId === jobsRequestId.current) setLoading(false);
    }
  };
  
  const loadMoreJobs = () => {
    fetchPostedJobs(jobsPage + 1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return { formatted: 'TBD', day: '', month: '', year: '', dayOfWeek: '', toString: function() { return 'TBD'; } };
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    const dayOfWeek = date.toLocaleString('en-US', { weekday: 'short' });
    return {
      formatted: `${day} ${month} ${year}`,
      day, month, year, dayOfWeek,
      toString: function() { return this.formatted; }
    };
  };

  const getDaysUntil = (dateString) => {
    if (!dateString) return null;
    const melbTZ = 'Australia/Melbourne';
    const todayMelb = new Date(new Date().toLocaleString('en-US', { timeZone: melbTZ }));
    const eventMelb = new Date(new Date(dateString).toLocaleString('en-US', { timeZone: melbTZ }));
    todayMelb.setHours(0, 0, 0, 0);
    eventMelb.setHours(0, 0, 0, 0);
    const diffTime = eventMelb - todayMelb;
    return Math.round(diffTime / (1000 * 60 * 60 * 24));
  };

  const getStatusConfig = (status) => {
    const configs = {
      PENDING_APPROVAL: {
        color: 'yellow',
        gradient: 'from-yellow-500 to-amber-500',
        bgLight: 'bg-yellow-50',
        bgDark: 'bg-yellow-900/20',
        textLight: 'text-yellow-700',
        textDark: 'text-yellow-300',
        borderLight: 'border-yellow-200',
        borderDark: 'border-yellow-500/30',
        label: 'Pending Approval',
        icon: AlertCircle
      },
      SEEKING_APPLICANTS: {
        color: 'purple',
        gradient: 'from-purple-500 to-fuchsia-500',
        bgLight: 'bg-purple-50',
        bgDark: 'bg-purple-900/20',
        textLight: 'text-purple-700',
        textDark: 'text-purple-300',
        borderLight: 'border-purple-200',
        borderDark: 'border-purple-500/30',
        label: 'Seeking Applicants',
        icon: Briefcase
      },
      PENDING_HIRE: {
        color: 'orange',
        gradient: 'from-orange-500 to-amber-500',
        bgLight: 'bg-orange-50',
        bgDark: 'bg-orange-900/20',
        textLight: 'text-orange-700',
        textDark: 'text-orange-300',
        borderLight: 'border-orange-200',
        borderDark: 'border-orange-500/30',
        label: 'Pending Hire',
        icon: AlertCircle
      },
      OFFER_SENT: {
        color: 'emerald',
        gradient: 'from-emerald-500 to-teal-500',
        bgLight: 'bg-emerald-50',
        bgDark: 'bg-emerald-900/20',
        textLight: 'text-emerald-700',
        textDark: 'text-emerald-300',
        borderLight: 'border-emerald-200',
        borderDark: 'border-emerald-500/30',
        label: 'Offer Sent',
        icon: Send
      },
      HIRED: {
        color: 'green',
        gradient: 'from-green-500 to-emerald-500',
        bgLight: 'bg-green-50',
        bgDark: 'bg-green-900/20',
        textLight: 'text-green-700',
        textDark: 'text-green-300',
        borderLight: 'border-green-200',
        borderDark: 'border-green-500/30',
        label: 'Hired',
        icon: UserCheck
      },
      PARTIALLY_HIRED: {
        color: 'teal',
        gradient: 'from-teal-500 to-cyan-500',
        bgLight: 'bg-teal-50',
        bgDark: 'bg-teal-900/20',
        textLight: 'text-teal-700',
        textDark: 'text-teal-300',
        borderLight: 'border-teal-200',
        borderDark: 'border-teal-500/30',
        label: 'Partially Hired',
        icon: Users
      },
      PENDING_COMPLETION: {
        color: 'indigo',
        gradient: 'from-indigo-500 to-purple-500',
        bgLight: 'bg-indigo-50',
        bgDark: 'bg-indigo-900/20',
        textLight: 'text-indigo-700',
        textDark: 'text-indigo-300',
        borderLight: 'border-indigo-200',
        borderDark: 'border-indigo-500/30',
        label: 'Pending Completion',
        icon: Clock
      },
      RELEASE_PAYMENT: {
        color: 'cyan',
        gradient: 'from-cyan-500 to-blue-500',
        bgLight: 'bg-cyan-50',
        bgDark: 'bg-cyan-900/20',
        textLight: 'text-cyan-700',
        textDark: 'text-cyan-300',
        borderLight: 'border-cyan-200',
        borderDark: 'border-cyan-500/30',
        label: 'Payment Processing',
        icon: Clock
      },
      PAID_COMPLETED: {
        color: 'green',
        gradient: 'from-green-500 to-emerald-500',
        bgLight: 'bg-green-50',
        bgDark: 'bg-green-900/20',
        textLight: 'text-green-700',
        textDark: 'text-green-300',
        borderLight: 'border-green-200',
        borderDark: 'border-green-500/30',
        label: 'Paid/Completed',
        icon: CheckCircle
      },
      CANCELLED: {
        color: 'red',
        gradient: 'from-red-500 to-rose-500',
        bgLight: 'bg-red-50',
        bgDark: 'bg-red-900/20',
        textLight: 'text-red-700',
        textDark: 'text-red-300',
        borderLight: 'border-red-200',
        borderDark: 'border-red-500/30',
        label: 'Cancelled',
        icon: X
      }
    };
    return configs[status] || configs.SEEKING_APPLICANTS;
  };

  const getJobTypeIcon = (type) => {
    const icons = {
      photographer: '📷',
      videographer: '🎥',
      editor: '✂️',
      assistant: '👤',
      drone_operator: '🚁'
    };
    return icons[type] || '📸';
  };

  // Derive per-role / per-applicant pending offer state from the jobOffers list
  // (fetched via GET /jobs/{job_id}/offers when the job details modal opens).
  // Used to (a) show "Offer Pending" badges on the right applicants and
  // (b) block sending duplicate offers for the same role on advanced jobs.
  const pendingOfferRoles = new Set();
  const pendingOfferApplicantIds = new Set();
  jobOffers.forEach((offer) => {
    if (offer?.offer_status !== 'pending') return;
    if (offer.role) {
      pendingOfferRoles.add(normalizeRole(offer.role));
    }
    const id = offer.crew_member_id || offer.applicant_id;
    if (id) pendingOfferApplicantIds.add(String(id));
  });
  // Fallback: include the job-level pending_offer summary as well, in case
  // the offers endpoint hasn't been re-fetched after a fresh send
  if (selectedJob?.pending_offer?.applicant_id) {
    pendingOfferApplicantIds.add(String(selectedJob.pending_offer.applicant_id));
  }
  if (selectedJob?.pending_offer?.role) {
    pendingOfferRoles.add(normalizeRole(selectedJob.pending_offer.role));
  }

  const filteredJobs = postedJobs
    .filter(job => {
      // Exclude fully-hired/confirmed jobs - they now appear in Job Comms
      // Also exclude RELEASE_PAYMENT - appears in Upcoming Bookings
      // KEEP PARTIALLY_HIRED visible: an advanced job in this state still has
      // open positions and the user is actively hiring the remaining crew.
      if (['HIRED', 'PENDING_COMPLETION', 'RELEASE_PAYMENT'].includes(job.status)) {
        return false;
      }

      // Search is applied server-side (fetchPostedJobs) across all the user's jobs

      // Filter by tab
      const isArchived = job.status === 'PAID_COMPLETED' || job.status === 'CANCELLED';
      const matchesTab = activeTab === 'archived' ? isArchived : !isArchived;

      return matchesTab;
    })
    .sort((a, b) => {
      // Sort by event date: upcoming events first (nearest date at top)
      const dateA = a.jobDate ? new Date(a.jobDate) : new Date(0);
      const dateB = b.jobDate ? new Date(b.jobDate) : new Date(0);
      const now = new Date();
      const aIsFuture = dateA >= now;
      const bIsFuture = dateB >= now;
      // Future events first, sorted ascending (nearest upcoming first)
      // Past events after, sorted descending (most recent past first)
      if (aIsFuture && !bIsFuture) return -1;
      if (!aIsFuture && bIsFuture) return 1;
      if (aIsFuture && bIsFuture) return dateA - dateB;
      return dateB - dateA;
    });

  // Tab counts (exclude statuses that live on other pages, matching filteredJobs)
  // Totals come from the server so they cover every job, not just the loaded pages
  const archivedJobsCount = tabCounts.archived;
  const activeJobsCount = tabCounts.active;

  const handleViewDetails = (job) => {
    setSelectedJob(job);
    setShowJobDetails(true);
    setDetailsTab('overview');
    setJobApplicants([]);
    setJobOffers([]);
    // Fetch applicants if job has any
    if (job.applicants > 0) {
      fetchJobApplicants(job.id);
    }
    // Fetch offers so we can show per-role pending state and block duplicate
    // offers for the same position on advanced jobs
    fetchJobOffers(job.id);
  };

  const fetchJobOffers = async (jobId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${jobId}/offers`
      );
      setJobOffers(response.data?.offers || []);
    } catch (error) {
      console.error('Error fetching job offers:', error);
      setJobOffers([]);
    }
  };

  const fetchJobApplicants = async (jobId) => {
    try {
      setLoadingApplicants(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${jobId}/applicants`
      );
      setJobApplicants(response.data.applicants || []);

      // Update selectedJob with shortlisted_users from response
      if (response.data.shortlisted_users) {
        setSelectedJob(prev => prev && prev.id === jobId
          ? { ...prev, shortlisted_users: response.data.shortlisted_users }
          : prev
        );
        // Also update in postedJobs array
        setPostedJobs(prev => prev.map(j =>
          j.id === jobId ? { ...j, shortlisted_users: response.data.shortlisted_users } : j
        ));
      }
    } catch (error) {
      console.error('Error fetching applicants:', error);
      toast.error('Failed to load applicants');
    } finally {
      setLoadingApplicants(false);
    }
  };

  const fetchFullApplicantProfile = async (userId) => {
    try {
      setLoadingFullProfile(true);

      // Fetch profile and reviews in parallel
      const [profileResponse, reviewsResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}`),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/${userId}`).catch(err => {
          console.log('Could not fetch reviews:', err);
          return { data: { reviews: [], stats: { average_rating: 0, total_reviews: 0, rating_breakdown: {} } } };
        })
      ]);

      // Merge profile data with reviews
      const profileWithReviews = {
        ...profileResponse.data,
        reviews: reviewsResponse.data.reviews || [],
        reviewStats: reviewsResponse.data.stats || { average_rating: 0, total_reviews: 0, rating_breakdown: {} }
      };

      setFullApplicantProfile(profileWithReviews);
    } catch (error) {
      console.error('Error fetching full profile:', error);
      toast.error('Failed to load full profile');
    } finally {
      setLoadingFullProfile(false);
    }
  };

  const handlePostJob = () => {
    // Block NEW and PENDING_APPROVAL users (using database status)
    if (userStatus === 'NEW' || userStatus === 'PENDING_APPROVAL') {
      console.log('🚫 Blocking post job for status:', userStatus);
      setShowProfileBlocker(true);
      return;
    }
    
    navigate('/post-job');
  };
  
  const calculateTotal = () => {
    const rate = parseFloat(jobForm.ratePerHour) || 0;
    const hours = parseFloat(jobForm.hours) || 0;
    return rate * hours;
  };
  
  const handleAddDate = () => {
    setJobForm({ ...jobForm, dates: [...jobForm.dates, ''] });
  };

  const handleRemoveDate = (index) => {
    setJobForm({ 
      ...jobForm, 
      dates: jobForm.dates.filter((_, i) => i !== index) 
    });
  };

  const handleDateChange = (index, value) => {
    const newDates = [...jobForm.dates];
    newDates[index] = value;
    setJobForm({ ...jobForm, dates: newDates });
  };
  
  const handleSubmitJob = async (formData) => {
    try {
      const userStr = localStorage.getItem('shootzu_user');
      const token = localStorage.getItem('shootzu_token');

      if (!userStr || !token) {
        toast.error('Please log in to post a job');
        window.location.href = '/login';
        return;
      }

      const user = JSON.parse(userStr);

      const jobData = {
        title: formData.title,
        description: formData.brief,
        location: formData.location,
        date: formData.dates[0], // Primary date for backward compatibility
        dates: formData.dates.filter(d => d), // Only include non-empty dates
        requiresWWCC: formData.requiresWWCC,
        budget: `$${formData.ratePerHour}/hr for ${formData.hours}hrs`,
        required_roles: [formData.type],
        status: 'open',
        poster_id: user.id,
        poster_name: user.full_name || `${user.first_name} ${user.last_name}`,
        type: formData.type,
        ratePerHour: parseFloat(formData.ratePerHour) || 0,
        hours: parseFloat(formData.hours) || 0
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/jobs`,
        jobData,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      const successMessage = formData.dates.filter(d => d).length > 1 
        ? `Multi-day job posted successfully! (${formData.dates.filter(d => d).length} days)`
        : 'Job posted successfully!';
      
      toast.success(successMessage);
      setShowPostJobModal(false);

      // Refresh the job list
      fetchPostedJobs(1);
    } catch (error) {
      console.error('Error posting job:', error);
      toast.error(error.response?.data?.detail || 'Failed to post job. Please try again.');
    }
  };
  
  const handleMessageCrew = async (job) => {
    // Block NEW and PENDING_APPROVAL users (using database status)
    if (userStatus === 'NEW' || userStatus === 'PENDING_APPROVAL') {
      console.log('🚫 Blocking message crew for status:', userStatus);
      setShowProfileBlocker(true);
      return;
    }
    
    if (!job.hiredCrew || !job.hiredCrew.email) {
      toast.error('Crew member information not available');
      return;
    }
    
    try {
      // Create or get conversation
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/messages/conversation/create`,
        {
          job_id: job.id,
          job_title: job.title,
          user1_email: user.email,
          user2_email: job.hiredCrew.email
        }
      );
      
      const convId = response.data.conversation?.id || response.data.conversation_id;
      if (convId) {
        // Navigate to messages page
        navigate("/messages", { state: { conversationId: convId, autoOpen: true } });
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      toast.error('Failed to open conversation');
    }
  };
  
  const handleCancelJob = async (jobId) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${jobId}/cancel`,
        {
          user_email: user.email
        }
      );
      
      toast.success('Job cancelled successfully');
      setShowJobDetails(false);
      fetchPostedJobs(1); // Refresh the job list
    } catch (error) {
      console.error('Error cancelling job:', error);
      toast.error('Failed to cancel job');
    }
  };
  
  const canCompleteJob = (jobDate) => {
    if (!jobDate) return false;
    const jobDateObj = new Date(jobDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    jobDateObj.setHours(0, 0, 0, 0);
    return jobDateObj <= today;
  };
  
  const formatJobDateForMessage = (dateString) => {
    if (!dateString) return 'TBD';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  return (
    <div className="min-h-screen" style={{ background: '#0A0E1A' }}>
      <Sidebar onLogout={onLogout} />
      <Header />

      <div className="cd-root sidebar-content pt-16 lg:pt-20">
        {/* Animated bloom blobs */}
        <div className="cd-bloom-wrap">
          <div className="cd-bloom cd-bloom-1" />
          <div className="cd-bloom cd-bloom-2" />
        </div>

        <div className="cd-main">
          {/* Header */}
          <div className="cd-col-mobile" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, marginBottom: 22 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span className="cd-display" style={{ fontWeight: 700, fontSize: 11, letterSpacing: 2, color: '#C98AD4' }}>YOUR PRODUCTIONS</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontWeight: 700, fontSize: 11.5, color: '#C98AD4', background: 'rgba(120,40,124,.25)', padding: '4px 11px', borderRadius: 999 }}>
                  {filteredJobs.length} active jobs
                </span>
              </div>
              <div className="cd-display" style={{ fontWeight: 800, fontSize: 32, letterSpacing: '-1px', color: '#fff', marginTop: 8 }}>Hire your crew</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: '0 0 auto' }}>
              <button
                type="button"
                aria-label="Notifications"
                style={{ width: 46, height: 46, borderRadius: 13, background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', position: 'relative' }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.94 1.94 0 0 0 3.4 0" /></svg>
                <span style={{ position: 'absolute', top: 11, right: 12, width: 8, height: 8, borderRadius: 999, background: '#28C0D0', border: '2px solid #0C1120' }} />
              </button>
              <button type="button" onClick={handlePostJob} className="cd-btn cd-btn-primary" style={{ padding: '13px 22px', fontSize: 14 }}>
                <Plus className="w-[18px] h-[18px]" />
                Post new job
              </button>
            </div>
          </div>

          {/* Tabs + Search */}
          <div className="cd-col-mobile" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
            <div className="cd-tabs">
              <button type="button" onClick={() => setActiveTab('active')} className={`cd-tab ${activeTab === 'active' ? 'cd-tab-active' : ''}`}>
                Active jobs<span className="cd-tab-count">{activeJobsCount}</span>
              </button>
              <button type="button" onClick={() => setActiveTab('archived')} className={`cd-tab ${activeTab === 'archived' ? 'cd-tab-active' : ''}`}>
                Archived{archivedJobsCount > 0 && <span className="cd-tab-count">{archivedJobsCount}</span>}
              </button>
            </div>
            <div style={{ position: 'relative', flex: 1, minWidth: 240, maxWidth: 380 }}>
              <Search style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', width: 17, height: 17, color: '#A8AEBC' }} />
              <input
                type="text"
                className="cd-input"
                placeholder="Search by job title, location, or type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ padding: '11px 14px 11px 40px' }}
              />
            </div>
          </div>

          {/* States */}
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="cd-skel">
                  <span style={{ width: 90, height: 64, borderRadius: 12, background: 'rgba(255,255,255,.08)', flex: '0 0 auto' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ width: 120, height: 11, borderRadius: 6, background: 'rgba(255,255,255,.08)', marginBottom: 10 }} />
                    <div style={{ width: '55%', height: 16, borderRadius: 6, background: 'rgba(255,255,255,.1)', marginBottom: 10 }} />
                    <div style={{ width: '38%', height: 10, borderRadius: 6, background: 'rgba(255,255,255,.07)' }} />
                  </div>
                  <div style={{ width: 80, height: 36, borderRadius: 10, background: 'rgba(255,255,255,.08)', flex: '0 0 auto' }} />
                </div>
              ))}
            </div>
          ) : filteredJobs.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '60px 20px', background: 'rgba(255,255,255,.025)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 18 }}>
              <span style={{ width: 64, height: 64, borderRadius: 18, background: 'rgba(40,192,208,.12)', color: '#28C0D0', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
                <Briefcase className="w-[30px] h-[30px]" />
              </span>
              <div className="cd-display" style={{ fontWeight: 700, fontSize: 19, color: '#fff' }}>
                {activeTab === 'archived' ? 'No archived jobs' : 'No jobs posted yet'}
              </div>
              <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,.55)', marginTop: 7, maxWidth: 340, lineHeight: 1.55 }}>
                Post your first job and start hiring vetted crew in minutes.
              </div>
              <button type="button" onClick={handlePostJob} className="cd-btn cd-btn-primary" style={{ marginTop: 20, padding: '12px 22px', fontSize: 13.5 }}>
                Post a job
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {filteredJobs.map((job) => {
                  const daysUntil = getDaysUntil(job.jobDate);
                  const isToday = daysUntil === 0;
                  const isUpcoming = daysUntil !== null && daysUntil > 0;
                  const isPast = daysUntil !== null && daysUntil < 0;
                  const hasHiredCrew = (job.isAdvanced && job.hiredCrewMembers?.length > 0 && ['HIRED', 'PARTIALLY_HIRED', 'PENDING_COMPLETION', 'PAID_COMPLETED'].includes(job.status)) || (!job.isAdvanced && job.hiredCrew && ['HIRED', 'PENDING_COMPLETION', 'PAID_COMPLETED'].includes(job.status));
                  const isOfferSent = job.status === 'OFFER_SENT';
                  const isPartial = job.status === 'PARTIALLY_HIRED';
                  const isSeeking = ['SEEKING_APPLICANTS', 'PENDING_APPROVAL'].includes(job.status) && !isOfferSent && !hasHiredCrew;
                  const hiredNames = job.isAdvanced && job.hiredCrewMembers?.length > 0
                    ? job.hiredCrewMembers.map(c => c.name).filter(Boolean).join(', ')
                    : job.hiredCrew?.name || null;

                  // Status presentation (Command Deck palette)
                  let stColor = '#60A5FA', stBg = 'rgba(37,99,235,.22)', stLabel = 'Seeking applicants';
                  if (job.status === 'CANCELLED') {
                    stColor = '#F87171'; stBg = 'rgba(185,28,28,.2)'; stLabel = 'Cancelled';
                  } else if (isOfferSent) {
                    stColor = '#C98AD4'; stBg = 'rgba(120,40,124,.3)'; stLabel = 'Offer sent';
                  } else if (isPartial) {
                    stColor = '#67C6B4'; stBg = 'rgba(103,198,180,.18)'; stLabel = 'Partially hired';
                  } else if (hasHiredCrew || ['HIRED', 'PAID_COMPLETED'].includes(job.status)) {
                    stColor = '#22D3EE'; stBg = 'rgba(14,116,144,.25)'; stLabel = 'Hired';
                  }

                  // Countdown strip colour
                  let stripColor = '#67C6B4', stripBg = 'rgba(103,198,180,.08)';
                  if (isToday) { stripColor = '#F87171'; stripBg = 'rgba(185,28,28,.14)'; }
                  else if (isUpcoming && daysUntil <= 3) { stripColor = '#FCD34D'; stripBg = 'rgba(180,83,9,.12)'; }
                  else if (isPast) { stripColor = 'rgba(255,255,255,.45)'; stripBg = 'rgba(255,255,255,.04)'; }

                  // Strip number + label text
                  let stripNum = '—', stripText = 'NO DATE';
                  if (isToday) { stripNum = 'NOW'; stripText = 'TODAY'; }
                  else if (isUpcoming) { stripNum = String(daysUntil); stripText = daysUntil === 1 ? 'DAY TO GO' : 'DAYS TO GO'; }
                  else if (isPast) { stripNum = String(Math.abs(daysUntil)); stripText = Math.abs(daysUntil) === 1 ? 'DAY AGO' : 'DAYS AGO'; }

                  // Action button
                  let actionLabel = 'View', actionPrimary = false;
                  if (isSeeking) { actionLabel = 'Review'; actionPrimary = true; }
                  else if (isPartial || (job.isAdvanced && !hasHiredCrew)) { actionLabel = 'Manage'; actionPrimary = true; }

                  // Advanced progress
                  const filledCount = job.hiredCrewMembers?.length || 0;
                  const totalCount = (job.crewMembers?.filter(cm => !cm.cancelled).length) || job.crewCount || 1;
                  const filledPct = totalCount > 0 ? Math.min(100, Math.round((filledCount / totalCount) * 100)) : 0;

                  return (
                    <div
                      key={job.id}
                      className="cd-card cd-col-mobile"
                      onClick={() => handleViewDetails(job)}
                      style={{ cursor: 'pointer' }}
                    >
                      {/* Countdown strip */}
                      <div className="cd-strip" style={{ background: stripBg }}>
                        <div className="cd-strip-num" style={{ color: stripColor }}>{stripNum}</div>
                        <div className="cd-strip-label" style={{ color: stripColor }}>{stripText}</div>
                      </div>

                      {/* Body */}
                      <div className="cd-col-mobile" style={{ flex: 1, minWidth: 0, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 18 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap', marginBottom: 7 }}>
                            <span className="cd-badge" style={{ color: stColor, background: stBg }}>
                              <span className="cd-badge-dot" style={{ background: stColor }} />
                              {stLabel}
                            </span>
                            {job.isAdvanced ? (
                              <span className="cd-pill" style={{ color: '#C98AD4', background: 'rgba(120,40,124,.25)', fontWeight: 700 }}>
                                {job.crewCount || job.hiredCrewMembers?.length || totalCount} crew
                              </span>
                            ) : (job.type && (
                              <span className="cd-pill" style={{ textTransform: 'capitalize' }}>
                                {job.type.replace(/_/g, ' ')}
                              </span>
                            ))}
                            <span className="cd-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.4)' }}>
                              {formatDate(job.jobDate).dayOfWeek}, {formatDate(job.jobDate).formatted}
                            </span>
                          </div>
                          <div className="cd-display" style={{ fontWeight: 700, fontSize: 18, color: '#fff' }}>{job.title}</div>

                          {job.isAdvanced ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
                              <div style={{ flex: 1, maxWidth: 220, height: 7, borderRadius: 999, background: 'rgba(255,255,255,.1)', overflow: 'hidden' }}>
                                <div style={{ width: `${filledPct}%`, height: '100%', background: 'linear-gradient(90deg,#572B7C,#28C0D0)', borderRadius: 999 }} />
                              </div>
                              <span style={{ fontSize: 12, color: 'rgba(255,255,255,.6)' }}>{filledCount}/{totalCount} positions filled</span>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap', color: 'rgba(255,255,255,.6)', fontSize: 13 }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Clock className="w-3.5 h-3.5" />{job.hours}h
                              </span>
                              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <MapPin className="w-3.5 h-3.5" />{job.location?.split(',')[0] || 'Location TBD'}
                              </span>
                              {hasHiredCrew && hiredNames && (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#67C6B4' }}>
                                  <Users className="w-3.5 h-3.5" />{hiredNames}
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Applicant count + price + action collapse into one row on mobile */}
                        <div className="cd-card-actions">
                        {/* Applicant count — seeking only */}
                        {isSeeking && (
                          <div style={{ textAlign: 'center', flex: '0 0 auto', padding: '0 8px' }}>
                            <div className="cd-display" style={{ fontWeight: 800, fontSize: 28, color: '#28C0D0', lineHeight: 1 }}>{job.applicants}</div>
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,.55)', marginTop: 2 }}>{job.applicants === 1 ? 'applicant' : 'applicants'}</div>
                          </div>
                        )}

                        {/* Payment */}
                        <div className="cd-card-pay" style={{ textAlign: 'right', flex: '0 0 auto' }}>
                          <div className="cd-display" style={{ fontWeight: 800, fontSize: 24, color: '#fff', letterSpacing: '-.5px', lineHeight: 1.1 }}>
                            ${(job.total || 0).toLocaleString()}
                          </div>
                          <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.5)' }}>
                            {job.isAdvanced
                              ? `${totalCount} role${totalCount !== 1 ? 's' : ''}`
                              : (job.ratePerHour > 0 ? `$${job.ratePerHour}/hr` : '')}
                          </div>
                        </div>

                        {/* Action */}
                        <div className="cd-card-act" style={{ display: 'flex', gap: 9, flex: '0 0 auto' }}>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleViewDetails(job); }}
                            className={actionPrimary ? 'cd-btn cd-btn-primary' : 'cd-btn'}
                          >
                            {actionLabel}
                          </button>
                          {job.status === 'PENDING_COMPLETION' && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setSelectedJob(job); setShowReviewModal(true); }}
                              className="cd-btn cd-btn-primary"
                              style={{ background: '#15803D', color: '#fff', boxShadow: 'none' }}
                            >
                              <Star className="w-3.5 h-3.5" />
                              Release
                            </button>
                          )}
                        </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Load More */}
              {hasMoreJobs && !loading && (
                <div style={{ marginTop: 28, textAlign: 'center' }}>
                  <button type="button" onClick={loadMoreJobs} className="cd-btn" style={{ padding: '12px 28px', fontSize: 14 }}>
                    Load more jobs
                  </button>
                </div>
              )}
            </>
          )}

          {/* Bottom CTA → Job Comms */}
          <div className="cd-col-mobile" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, marginTop: 20, padding: '20px 24px', borderRadius: 16, background: 'linear-gradient(120deg,rgba(103,198,180,.14),rgba(40,192,208,.05))', border: '1px solid rgba(103,198,180,.25)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <span style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(103,198,180,.18)', color: '#67C6B4', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                <MessageSquare className="w-[22px] h-[22px]" />
              </span>
              <div>
                <div className="cd-display" style={{ fontWeight: 700, fontSize: 15, color: '#fff' }}>Coordinating a confirmed job?</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,.6)', marginTop: 2 }}>Chat, share docs and release payment in Job Comms.</div>
              </div>
            </div>
            <button type="button" onClick={() => navigate('/job-comms')} className="cd-btn" style={{ flex: '0 0 auto' }}>
              Open Job Comms
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Job Details Modal - Using inline version for compatibility */}
      {showJobDetails && selectedJob && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[70] flex items-center justify-center p-[2.5vw]"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowJobDetails(false);
          }}
          style={{ overflow: 'hidden', touchAction: 'none' }}
        >
          <div
            className="w-[95vw] h-[88dvh] lg:w-full lg:h-auto rounded-2xl lg:rounded-3xl shadow-2xl lg:max-w-7xl lg:max-h-[85dvh] flex flex-col overflow-hidden"
            style={{ touchAction: 'pan-y', background: '#12162A', border: '1px solid rgba(255,255,255,.12)' }}
          >

            {/* Header — Command Deck violet→magenta band */}
            <div className="relative flex-shrink-0 px-4 py-3 lg:px-6 lg:py-4" style={{ background: 'linear-gradient(120deg,#572B7C,#78287C)', borderBottom: '1px solid rgba(255,255,255,.12)' }}>
              <div className="absolute inset-0 bg-gradient-to-b from-white/[0.04] to-transparent pointer-events-none"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className="bg-white/20 backdrop-blur-sm text-white border-white/30 font-mono text-[10px] px-2 py-0.5">
                        {selectedJob.jobNumber || selectedJob.id.slice(0, 8).toUpperCase()}
                      </Badge>
                      <Badge className="bg-white/90 text-gray-900 font-semibold flex items-center gap-1 text-[10px] px-2 py-0.5">
                        {(() => {
                          const StatusIcon = getStatusConfig(selectedJob.status).icon;
                          return <StatusIcon className="w-3 h-3" />;
                        })()}
                        <span className="hidden sm:inline">{getStatusConfig(selectedJob.status).label}</span>
                      </Badge>
                      {selectedJob.type && (
                        <Badge className="bg-white/90 text-gray-900 font-bold capitalize text-[10px] px-2 py-0.5">
                          {selectedJob.type.replace('_', ' ')}
                        </Badge>
                      )}
                    </div>
                    <h2 className="text-lg lg:text-2xl font-bold text-white truncate" style={{ fontFamily: 'Space Grotesk' }}>
                      {selectedJob.title}
                    </h2>
                    <div className="flex items-center gap-1.5 text-white/80 mt-0.5">
                      <Calendar className="w-3.5 h-3.5" />
                      <span className="text-xs lg:text-sm font-medium">
                        {formatDate(selectedJob.jobDate).formatted}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowJobDetails(false)}
                    className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-1.5 lg:p-2 rounded-full transition-all flex-shrink-0"
                  >
                    <X className="w-5 h-5 lg:w-6 lg:h-6" />
                  </button>
                </div>
              </div>
            </div>

            {/* Content: Desktop 2-Column, Mobile Tabbed - Full height container */}
            <div className="flex-1 flex flex-col lg:flex-row overflow-hidden min-h-0">
              
              {/* Mobile Tabs - Only visible on mobile */}
              <div className="lg:hidden flex-shrink-0 border-b border-white/[0.08] bg-transparent">
                <div className="flex">
                  <button
                    onClick={() => setMobileTab('details')}
                    className={`flex-1 py-4 px-4 text-sm font-semibold transition-all ${
                      mobileTab === 'details'
                        ? 'text-[#28C0D0] border-b-2 border-[#28C0D0] bg-[rgba(40,192,208,0.08)]'
                        : 'text-gray-400'
                    }`}
                  >
                    Job Details
                  </button>
                  {selectedJob.applicants > 0 && (
                    <button
                      onClick={() => setMobileTab('applicants')}
                      className={`flex-1 py-4 px-4 text-sm font-semibold transition-all relative ${
                        mobileTab === 'applicants'
                          ? 'text-[#28C0D0] border-b-2 border-[#28C0D0] bg-[rgba(40,192,208,0.08)]'
                          : 'text-gray-400'
                      }`}
                    >
                      Applicants
                      <span className="ml-2 inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-[#0C1120] bg-[#28C0D0] rounded-full">
                        {selectedJob.applicants}
                      </span>
                    </button>
                  )}
                </div>
              </div>
              
              {/* LEFT COLUMN: Job Details - Hidden on mobile when applicants tab active, scrollable */}
              <div 
                className={`flex-1 p-4 lg:p-6 space-y-4 lg:space-y-6 overflow-y-auto min-h-0 ${mobileTab === 'applicants' ? 'hidden lg:block' : 'block'}`}
                style={{ 
                  WebkitOverflowScrolling: 'touch',
                  overscrollBehavior: 'contain',
                  touchAction: 'pan-y'
                }}
              >
                  {/* Quick Stats Grid - 2 cols mobile, 4 cols desktop */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-3">
                    <div className={`${'bg-[rgba(255,255,255,0.045)] border border-white/[0.09]'} rounded-xl p-3`}>
                      <Users className="w-4 h-4 text-[#28C0D0] mb-1" />
                      <p className={`text-[11px] font-medium ${'text-gray-400'}`}>Applicants</p>
                      <p className={`text-lg font-bold ${'text-white'} mt-0.5`}>
                        {selectedJob.applicants}
                      </p>
                    </div>
                    <div className={`${'bg-[rgba(255,255,255,0.045)] border border-white/[0.09]'} rounded-xl p-3`}>
                      <Clock className="w-4 h-4 text-blue-500 mb-1" />
                      <p className={`text-[11px] font-medium ${'text-gray-400'}`}>
                        {selectedJob.isAdvanced ? 'Job Days' : 'Duration'}
                      </p>
                      <p className={`text-sm font-bold ${'text-white'} mt-0.5`}>
                        {selectedJob.isAdvanced
                          ? `${selectedJob.dates?.length || 1} day${(selectedJob.dates?.length || 1) > 1 ? 's' : ''}`
                          : `${selectedJob.hours} hours`
                        }
                      </p>
                    </div>
                    {!selectedJob.isAdvanced && (
                      <div className={`${'bg-[rgba(255,255,255,0.045)] border border-white/[0.09]'} rounded-xl p-3`}>
                        <DollarSign className="w-4 h-4 text-green-500 mb-1" />
                        <p className={`text-[11px] font-medium ${'text-gray-400'}`}>Hourly Rate</p>
                        <p className="text-sm font-bold text-[#67C6B4] mt-0.5">
                          ${selectedJob.ratePerHour}
                        </p>
                      </div>
                    )}
                    <div className="bg-[rgba(255,255,255,0.045)] border border-white/[0.09] rounded-xl p-3">
                      <Award className="w-4 h-4 text-yellow-500 mb-1" />
                      <p className={`text-[11px] font-medium ${'text-gray-300'}`}>
                        {selectedJob.isAdvanced ? 'Total Budget' : 'Total Cost'}
                      </p>
                      <p className="text-sm font-bold text-[#67C6B4] mt-0.5">
                        ${(selectedJob.total || selectedJob.budget || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {/* Offer Sent Banner - Show when offer is sent but not yet accepted - MOVED TO TOP */}
                  {(selectedJob.status === 'OFFER_SENT' || selectedJob.pending_offer) && !selectedJob.hiredCrew && (
                    <div className="relative overflow-hidden bg-[rgba(40,192,208,0.06)] border-[rgba(40,192,208,0.3)] border-2 rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg">
                      {/* Animated pulse background */}
                      <div className="absolute inset-0 bg-[rgba(40,192,208,0.06)] animate-pulse" />

                      {/* Header with prominent badge */}
                      <div className="relative flex items-center gap-4 mb-4">
                        <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-[rgba(40,192,208,0.2)] ring-2 ring-[rgba(40,192,208,0.5)] shadow-lg">
                          <Send className="w-7 h-7 text-[#28C0D0]" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-xl sm:text-2xl font-black tracking-tight text-[#28C0D0]">
                              OFFER SENT
                            </h3>
                            <span className="px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide bg-[rgba(40,192,208,0.2)] text-[#28C0D0]">
                              Pending
                            </span>
                          </div>
                          <p className="text-sm font-medium text-[#28C0D0]/80">
                            Awaiting crew member response
                          </p>
                        </div>
                      </div>

                      {/* Recipient card */}
                      {selectedJob.pending_offer && (
                        <div className="relative p-4 rounded-xl bg-black/30 border border-[rgba(40,192,208,0.3)] backdrop-blur-sm">
                          <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-full flex items-center justify-center shadow-md" style={{ background: 'linear-gradient(135deg,#572B7C,#28C0D0)' }}>
                              <User className="w-7 h-7 text-white" />
                            </div>
                            <div className="flex-1">
                              <p className={`text-lg font-bold ${'text-white'}`}>
                                {selectedJob.pending_offer.applicant_name}
                              </p>
                              <p className={`text-sm ${'text-gray-400'}`}>
                                {selectedJob.pending_offer.applicant_email}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[rgba(40,192,208,0.2)]">
                            <Clock className="w-5 h-5 text-[#28C0D0]" />
                            <span className="text-sm font-semibold text-[#28C0D0]">
                              48 hours to respond
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Advanced Job: Crew Roles Overview - Cleaner style like Admin */}
                  {selectedJob.isAdvanced && selectedJob.crewMembers && selectedJob.crewMembers.length > 0 && (
                    <div className="bg-[rgba(255,255,255,0.045)] border border-white/[0.09] rounded-2xl p-4 lg:p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <Users className="w-5 h-5 text-indigo-600" />
                        <h3 className={`text-lg font-bold ${'text-white'}`}>
                          Crew Roles ({selectedJob.crewMembers.filter(cm => !cm.cancelled).length})
                        </h3>
                      </div>
                      <div className="space-y-3">
                        {selectedJob.crewMembers.map((member, idx) => {
                          if (member.cancelled) return null;
                          // Get applicant count for this specific role (if available)
                          const roleApplicants = selectedJob.applicantsByRole?.[member.role?.toLowerCase()] || 0;
                          const hasHiredForRole = member.hired || selectedJob.hiredByRole?.[member.role?.toLowerCase()];

                          // Cancel-position eligibility: unhired, no pending offer for the role, status allows it
                          const cmRoleNorm = normalizeRole(member.role);
                          const pendingOfferForRole = cmRoleNorm
                            ? jobOffers.find((o) => {
                                if (o?.offer_status !== 'pending') return false;
                                return normalizeRole(o.role) === cmRoleNorm;
                              })
                            : null;
                          const cancelBlockedStatuses = ['CANCELLED', 'PAID_COMPLETED', 'RELEASE_PAYMENT', 'PENDING_COMPLETION'];
                          const canCancelThisPosition = !member.hired
                            && !cancelBlockedStatuses.includes(selectedJob.status)
                            && !pendingOfferForRole;

                          return (
                            <div key={idx} className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center text-white font-bold">
                                    {idx + 1}
                                  </div>
                                  <div>
                                    <h4 className={`font-bold capitalize ${'text-white'}`}>
                                      {member.role?.replace(/_/g, ' ') || 'Crew Member'}
                                    </h4>
                                    <div className="flex items-center gap-2 mt-1">
                                      {hasHiredForRole ? (
                                        <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 text-xs">
                                          <UserCheck className="w-3 h-3 mr-1" />
                                          Hired
                                        </Badge>
                                      ) : pendingOfferForRole ? (
                                        <Badge className="bg-[rgba(40,192,208,0.12)] text-[#28C0D0] text-xs">
                                          <Clock className="w-3 h-3 mr-1" />
                                          Offer pending
                                        </Badge>
                                      ) : (
                                        <Badge className={`${roleApplicants > 0 ? 'bg-[rgba(40,192,208,0.12)] text-[#28C0D0]' : 'bg-white/[0.06] text-gray-400'} text-xs`}>
                                          <User className="w-3 h-3 mr-1" />
                                          {roleApplicants} applicant{roleApplicants !== 1 ? 's' : ''}
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex flex-col items-end gap-1">
                                  <p className="text-xl font-black text-indigo-600 dark:text-indigo-400">
                                    ${(member.subtotal || (member.hours * member.ratePerHour) || 0).toLocaleString()}
                                  </p>
                                  {canCancelThisPosition && (
                                    <button
                                      onClick={() => {
                                        setCancellingPosition({ ...member, originalIndex: idx });
                                        setCancellingPositionIndex(idx);
                                        setShowCancelPositionModal(true);
                                      }}
                                      className="text-xs text-red-500 hover:text-red-700 hover:underline font-medium whitespace-nowrap"
                                    >
                                      Cancel Position
                                    </button>
                                  )}
                                </div>
                              </div>
                              {member.dateAssignments && member.dateAssignments.filter(a => a.hours > 0).length > 0 && (
                                <div className="mt-2 pl-13 space-y-0.5">
                                  {member.dateAssignments.filter(a => a.hours > 0).map((a, i) => (
                                    <div key={i} className="flex items-center gap-1.5 text-[11px] text-indigo-300/70">
                                      <Calendar className="w-3 h-3" />
                                      <span>{formatDate(a.date).formatted}</span>
                                      <span className="text-indigo-400/50">&mdash;</span>
                                      <span className="font-medium">{a.hours} hrs</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Total Budget Summary */}
                      <div className="mt-4 p-4 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white/90 text-sm font-medium">Total Budget</p>
                            <p className="text-white/70 text-xs mt-0.5">
                              {selectedJob.crewMembers.length} role{selectedJob.crewMembers.length !== 1 ? 's' : ''}
                            </p>
                          </div>
                          <p className="text-white text-2xl font-black">
                            ${(selectedJob.total || selectedJob.budget || 0).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

              {/* Hired Crew Info - For Advanced Jobs (PARTIALLY_HIRED, HIRED, PENDING_COMPLETION) show all hired crew members */}
              {selectedJob.isAdvanced && selectedJob.hiredCrewMembers && selectedJob.hiredCrewMembers.length > 0 && (
                <div className="bg-[rgba(255,255,255,0.045)] border-white/[0.09] border rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-[rgba(40,192,208,0.12)]">
                        <UserCheck className="w-5 h-5 text-[#28C0D0]" />
                      </div>
                      <div>
                        <h3 className={`text-base sm:text-lg font-bold ${'text-white'}`}>
                          Hired Crew
                        </h3>
                        <p className={`text-xs ${'text-gray-400'}`}>
                          {selectedJob.hiredCrewMembers.length}/{selectedJob.crewMembers?.filter(cm => !cm.cancelled).length || 1} Positions Filled
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {selectedJob.hiredCrewMembers.map((crew, idx) => {
                      // Check if job date has passed (for showing release payment button)
                      const jobDate = selectedJob.jobDate || selectedJob.dates?.[0];
                      const jobDatePassed = jobDate && new Date(jobDate) < new Date();
                      const canReleasePayment = jobDatePassed &&
                        (selectedJob.status === 'HIRED' || selectedJob.status === 'PENDING_COMPLETION') &&
                        !crew.paymentReleased;

                      return (
                        <div key={idx} className="p-4 bg-white/[0.04] border-white/[0.08] border rounded-xl">
                          <div className="flex items-start gap-4">
                            {/* Profile Photo */}
                            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden border-3 border-[rgba(40,192,208,0.5)] bg-white/[0.05] flex-shrink-0 shadow-md">
                              {crew.photo && crew.photo !== 'https://via.placeholder.com/150' ? (
                                <img
                                  src={crew.photo}
                                  alt={crew.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div className={`w-full h-full items-center justify-center text-white font-bold text-xl ${crew.photo && crew.photo !== 'https://via.placeholder.com/150' ? 'hidden' : 'flex'}`} style={{ background: 'linear-gradient(135deg,#572B7C,#28C0D0)' }}>
                                {crew.name?.charAt(0) || '?'}
                              </div>
                            </div>

                            {/* Crew Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <p className={`text-base sm:text-lg font-bold ${'text-white'}`}>
                                    {crew.name}
                                  </p>
                                  <p className="text-sm font-medium capitalize text-[#28C0D0]">
                                    {crew.role}
                                  </p>
                                </div>
                                <div className="flex flex-col items-end gap-1">
                                  {crew.paymentReleased ? (
                                    <Badge className="bg-purple-100 text-purple-700 text-xs px-2 py-1">
                                      Payment Released
                                    </Badge>
                                  ) : (
                                    <Badge className="bg-[rgba(40,192,208,0.12)] text-[#28C0D0] text-xs px-2 py-1">
                                      Hired
                                    </Badge>
                                  )}
                                  <p className="text-sm font-bold text-[#67C6B4]">
                                    ${crew.subtotal?.toLocaleString() || '0'}
                                  </p>
                                </div>
                              </div>

                              {/* Contact Info & Actions */}
                              <div className={`flex flex-wrap items-center gap-2 mt-3 pt-3 border-t ${'border-white/[0.08]'}`}>
                                {/* Phone */}
                                {crew.phone && (
                                  <a
                                    href={`tel:${crew.phone}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all bg-white/5 hover:bg-white/10 text-gray-200 border border-white/10"
                                    title={`Call ${crew.phone}`}
                                  >
                                    <Phone className="w-4 h-4 text-[#28C0D0]" />
                                    <span className="text-xs font-medium">{crew.phone}</span>
                                  </a>
                                )}

                                {/* Email */}
                                {crew.email && (
                                  <a
                                    href={`mailto:${crew.email}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all bg-white/5 hover:bg-white/10 text-gray-200 border border-white/10"
                                    title={`Email ${crew.email}`}
                                  >
                                    <Mail className="w-4 h-4 text-[#28C0D0]" />
                                    <span className="text-xs font-medium truncate max-w-[150px]">{crew.email}</span>
                                  </a>
                                )}

                                {/* Message Button */}
                                {crew.email && (
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        const resp = await axios.post(
                                          `${process.env.REACT_APP_BACKEND_URL}/api/messages/conversation/create`,
                                          {
                                            job_id: selectedJob.id,
                                            job_title: selectedJob.title,
                                            user1_email: user.email,
                                            user2_email: crew.email
                                          }
                                        );
                                        const convId = resp.data.conversation?.id || resp.data.conversation_id;
                                        if (convId) {
                                          navigate("/messages", { state: { conversationId: convId, autoOpen: true } });
                                        }
                                      } catch (err) {
                                        console.error('Error creating conversation:', err);
                                        toast.error('Failed to open conversation');
                                      }
                                    }}
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all bg-[#28C0D0] hover:bg-[#22aab8] text-[#0C1120]"
                                    title={`Message ${crew.name}`}
                                  >
                                    <MessageSquare className="w-4 h-4" />
                                    <span className="text-xs font-medium">Message</span>
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Release Payment Button - show only when job date passed and payment not released */}
                          {canReleasePayment && (
                            <div className={`mt-4 pt-4 border-t ${'border-white/[0.08]'}`}>
                              <Button
                                onClick={() => {
                                  setReviewingCrewMember(crew);
                                  setShowReviewModal(true);
                                }}
                                className="w-full h-11 text-sm font-bold bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white shadow-lg rounded-xl"
                              >
                                <Star className="w-4 h-4 mr-2" />
                                Release Payment for {crew.role}
                              </Button>
                              <p className={`text-xs ${'text-gray-400'} text-center mt-2`}>
                                Payout: ${crew.crewPayout?.toLocaleString() || (crew.subtotal * 0.98).toFixed(2)}
                              </p>
                            </div>
                          )}

                          {/* Show "Awaiting Job Completion" if job date hasn't passed */}
                          {!jobDatePassed && (selectedJob.status === 'HIRED' || selectedJob.status === 'PARTIALLY_HIRED') && (
                            <div className={`mt-3 pt-3 border-t ${'border-white/[0.08]'}`}>
                              <div className="flex items-center justify-between">
                                <p className={`text-xs ${'text-amber-400'} flex items-center gap-1`}>
                                  <Clock className="w-3 h-3" />
                                  Payment available after job date ({new Date(jobDate).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' })})
                                </p>
                                {!crew.paymentReleased && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCancellingCrewMember(crew);
                                      setCancellingCrewMemberIndex(crew.originalIndex);
                                      setShowCancelCrewMemberModal(true);
                                    }}
                                    className="text-xs text-red-500 hover:text-red-700 font-medium px-2 py-1 rounded hover:bg-red-50 transition-colors"
                                  >
                                    Cancel Position
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {/* Show unfilled positions (exclude cancelled) */}
                  {selectedJob.crewMembers && selectedJob.crewMembers
                    .map((cm, originalIndex) => ({ ...cm, originalIndex }))
                    .filter(cm => !cm.hired && !cm.cancelled).length > 0 && (
                    <div className={`mt-4 pt-4 border-t ${'border-white/[0.08]'}`}>
                      <p className={`text-sm ${'text-amber-400'} font-medium mb-2`}>
                        Positions Still Open:
                      </p>
                      <div className="space-y-2">
                        {selectedJob.crewMembers
                          .map((cm, originalIndex) => ({ ...cm, originalIndex }))
                          .filter(cm => !cm.hired && !cm.cancelled)
                          .map((cm) => {
                            // Look for a pending offer matching this open position's role
                            const cmRoleNormalized = normalizeRole(cm.role);
                            const pendingOffer = cmRoleNormalized
                              ? jobOffers.find((o) => {
                                  if (o?.offer_status !== 'pending') return false;
                                  return normalizeRole(o.role) === cmRoleNormalized;
                                })
                              : null;

                            const cancelBlockedStatuses = ['CANCELLED', 'PAID_COMPLETED', 'RELEASE_PAYMENT', 'PENDING_COMPLETION'];
                            const canCancel = !cancelBlockedStatuses.includes(selectedJob.status) && !pendingOffer;

                            return (
                              <div key={cm.originalIndex} className="flex items-center justify-between gap-2 p-2 rounded-lg bg-white/[0.04]">
                                {pendingOffer ? (
                                  <Badge className="bg-[rgba(40,192,208,0.12)] text-[#28C0D0] text-xs px-2 py-1 flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {cm.role} — Offer pending with {pendingOffer.crew_member_name || pendingOffer.applicant_name || 'applicant'}
                                  </Badge>
                                ) : (
                                  <Badge className="bg-amber-100 text-amber-700 text-xs px-2 py-1">
                                    {cm.role} - ${cm.subtotal?.toLocaleString() || '0'}
                                  </Badge>
                                )}
                                {canCancel && (
                                  <button
                                    onClick={() => {
                                      setCancellingPosition(cm);
                                      setCancellingPositionIndex(cm.originalIndex);
                                      setShowCancelPositionModal(true);
                                    }}
                                    className="text-xs text-red-500 hover:text-red-700 hover:underline font-medium whitespace-nowrap"
                                  >
                                    Cancel Position
                                  </button>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Hired Crew Info - For Simple Jobs (single crew member) */}
              {!selectedJob.isAdvanced && selectedJob.hiredCrew && (
                <div className={`${getStatusConfig(selectedJob.status).bgDark} ${getStatusConfig(selectedJob.status).borderDark} border rounded-xl sm:rounded-2xl p-4 sm:p-6`}>
                  <div className="flex items-center gap-3 mb-3">
                    <UserCheck className="w-4 sm:w-5 h-4 sm:h-5 text-[#28C0D0]" />
                    <h3 className={`text-base sm:text-lg font-bold ${'text-white'}`}>
                      {(selectedJob.status === 'HIRED' || selectedJob.status === 'PENDING_COMPLETION' || selectedJob.status === 'PAID_COMPLETED') ? 'Hired Crew Member' : 'Offer Sent'}
                    </h3>
                  </div>
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden border-2 border-[rgba(40,192,208,0.35)] bg-white/[0.05] flex-shrink-0">
                      {selectedJob.hiredCrew.photo && selectedJob.hiredCrew.photo !== 'https://via.placeholder.com/150' ? (
                        <img
                          src={selectedJob.hiredCrew.photo}
                          alt={selectedJob.hiredCrew.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className={`w-full h-full bg-gradient-to-br ${getStatusConfig(selectedJob.status).gradient} items-center justify-center text-white font-bold text-xl ${selectedJob.hiredCrew.photo && selectedJob.hiredCrew.photo !== 'https://via.placeholder.com/150' ? 'hidden' : 'flex'}`}>
                        {selectedJob.hiredCrew.name.charAt(0)}
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className={`text-base sm:text-lg font-bold ${'text-white'} mb-1`}>
                        {selectedJob.hiredCrew.name}
                      </p>
                      <p className={`text-sm ${'text-gray-400'} mb-2`}>
                        {selectedJob.hiredCrew.role}
                      </p>
                      {/* Contact Details */}
                      {selectedJob.hiredCrew.phone && (
                        <p className={`text-sm ${'text-[#28C0D0]'} font-medium mb-1`}>
                          {selectedJob.hiredCrew.phone}
                        </p>
                      )}
                      {selectedJob.hiredCrew.email && (
                        <p className={`text-sm ${'text-[#28C0D0]'} font-medium`}>
                          {selectedJob.hiredCrew.email}
                        </p>
                      )}
                      <Badge className={`mt-2 ${
                        (selectedJob.status === 'HIRED' || selectedJob.status === 'PENDING_COMPLETION' || selectedJob.status === 'PAID_COMPLETED')
                          ? 'bg-green-100 text-green-700'
                          : 'bg-orange-100 text-orange-700'
                      } text-xs sm:text-sm px-2 sm:px-3 py-1`}>
                        {(selectedJob.status === 'HIRED' || selectedJob.status === 'PENDING_COMPLETION' || selectedJob.status === 'PAID_COMPLETED') ? 'Confirmed & Hired' : 'Awaiting Response'}
                      </Badge>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMessageCrew(selectedJob);
                      }}
                      className="border-[rgba(40,192,208,0.4)] h-9 sm:h-10"
                    >
                      <MessageSquare className="w-4 h-4 mr-1 sm:mr-2" />
                      <span className="hidden sm:inline">Message</span>
                    </Button>
                  </div>
                </div>
              )}

              {/* Location */}
              <div className={`${'bg-[rgba(255,255,255,0.045)] border border-white/[0.09]'} rounded-2xl p-6`}>
                <div className="flex items-center gap-3 mb-2">
                  <MapPin className="w-5 h-5 text-[#28C0D0]" />
                  <h3 className={`text-lg font-bold ${'text-white'}`}>Location</h3>
                </div>
                <p className={`text-base ${'text-gray-300'}`}>
                  {selectedJob.location}
                </p>
              </div>

              {/* Job Description */}
              <div className={`${'bg-[rgba(255,255,255,0.045)] border border-white/[0.09]'} rounded-2xl p-6`}>
                <div className="flex items-center gap-3 mb-4">
                  <Briefcase className="w-5 h-5 text-[#28C0D0]" />
                  <h3 className={`text-lg font-bold ${'text-white'}`}>Job Description</h3>
                </div>
                <p className={`text-base leading-relaxed whitespace-pre-wrap ${'text-gray-300'}`}>
                  {linkifyText(selectedJob.description || 'No description provided')}
                </p>
              </div>

              {/* Payment Breakdown */}
              <div className="bg-[rgba(255,255,255,0.045)] border border-white/[0.09] rounded-2xl p-6">
                <h3 className={`text-lg font-bold mb-4 ${'text-white'}`}>
                  Payment Breakdown
                </h3>
                <div className="space-y-3">
                  {/* Only show rate/hours for standard jobs, not advanced jobs */}
                  {!selectedJob.isAdvanced && (
                    <>
                      <div className="flex justify-between items-center">
                        <span className={'text-gray-300'}>
                          Rate per hour
                        </span>
                        <span className="font-semibold text-[#67C6B4]">
                          ${selectedJob.ratePerHour}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className={'text-gray-300'}>
                          Total hours
                        </span>
                        <span className={`font-semibold ${'text-white'}`}>
                          {selectedJob.hours}h
                        </span>
                      </div>
                    </>
                  )}
                  <div className={`${!selectedJob.isAdvanced ? 'border-t border-white/[0.09] pt-3' : ''}`}>
                    <div className="flex justify-between items-center">
                      <span className={`text-lg font-bold ${'text-white'}`}>
                        {selectedJob.isAdvanced ? 'Total Budget' : 'Total Cost'}
                      </span>
                      <span className="text-2xl font-bold text-[#67C6B4]">
                        ${(selectedJob.total || selectedJob.budget || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 lg:gap-3 pt-2 lg:pt-4 flex-wrap">
                <Button
                  onClick={() => setShowJobDetails(false)}
                  variant="outline"
                  className="flex-1 h-12 text-sm lg:text-base font-semibold border-[rgba(40,192,208,0.4)] text-[#28C0D0] hover:bg-[rgba(40,192,208,0.1)]"
                >
                  Close
                </Button>

                {/* Share Job Button - Always Available */}
                <Button
                  onClick={(e) => handleShareJob(selectedJob.id, e)}
                  variant="outline"
                  className="h-12 text-sm lg:text-base font-semibold border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                >
                  {copiedJobId === selectedJob.id ? (
                    <>
                      <CheckIcon className="w-4 lg:w-5 h-4 lg:h-5 mr-2 text-green-500" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Share2 className="w-4 lg:w-5 h-4 lg:h-5 mr-2" />
                      Share Job
                    </>
                  )}
                </Button>

                {/* Edit/Delete buttons - Available for most statuses except when offers sent or completed */}
                {(selectedJob.status === 'PENDING_APPROVAL' || 
                  selectedJob.status === 'SEEKING_APPLICANTS') && 
                  !selectedJob.offer_sent && (
                  <Button
                    onClick={() => {
                      setShowJobDetails(false);
                      setEditingJob(selectedJob);
                      setShowEditJobModal(true);
                    }}
                    variant="outline"
                    className="flex-1 h-12 text-sm lg:text-base font-semibold border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                  >
                    <Edit className="w-4 lg:w-5 h-4 lg:h-5 mr-2" />
                    Edit Job
                  </Button>
                )}
                
                {/* Cancel/Delete Whole Job - Owner-side only allowed before any hire.
                    Once any crew member is hired, only an admin can cancel the whole job.
                    Owners can still cancel individual unhired positions via the Crew Roles section. */}
                {(selectedJob.status === 'PENDING_APPROVAL' ||
                  selectedJob.status === 'SEEKING_APPLICANTS') &&
                  !selectedJob.offer_sent &&
                  !selectedJob.hiredCrew &&
                  !(selectedJob.crewMembers || []).some(cm => cm.hired) && (
                  <Button
                    onClick={() => {
                      if (window.confirm(`Are you sure you want to cancel "${selectedJob.title}"? This cannot be undone.`)) {
                        handleCancelJob(selectedJob.id);
                      }
                    }}
                    variant="outline"
                    className="flex-1 h-12 text-sm lg:text-base font-semibold border-red-300 text-red-700 hover:bg-red-50"
                  >
                    <X className="w-4 lg:w-5 h-4 lg:h-5 mr-2" />
                    Cancel Job
                  </Button>
                )}
                {selectedJob.status === 'hired' && (
                  <>
                    {canCompleteJob(selectedJob.jobDate) ? (
                      <Button
                        onClick={() => setShowCompleteJobModal(true)}
                        className="flex-1 h-12 text-sm lg:text-base font-bold bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg shadow-green-500/50"
                      >
                        <CheckCircle className="w-4 lg:w-5 h-4 lg:h-5 mr-2" />
                        Complete Job
                      </Button>
                    ) : (
                      <div className="flex-1">
                        <Button
                          disabled
                          className="w-full h-12 text-sm lg:text-base font-bold bg-gray-400 text-gray-600 cursor-not-allowed opacity-60"
                        >
                          <CheckCircle className="w-4 lg:w-5 h-4 lg:h-5 mr-2" />
                          Complete Job
                        </Button>
                        <p className={`text-xs mt-2 text-center ${'text-yellow-400'} font-medium`}>
                          You are unable to complete this job until {formatJobDateForMessage(selectedJob.jobDate)}
                        </p>
                      </div>
                    )}
                  </>
                )}
                {(selectedJob.status === 'PENDING_COMPLETION' || selectedJob.status === 'pending_completion') && (
                  <Button
                    onClick={() => setShowReviewModal(true)}
                    className="flex-1 h-12 text-sm lg:text-base font-bold bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white shadow-lg shadow-orange-500/50"
                  >
                    <Star className="w-4 lg:w-5 h-4 lg:h-5 mr-2" />
                    Release Payment
                  </Button>
                )}
              </div>
              <p className="text-[8px] text-center py-1 opacity-20 select-none">modal:hire-crew-job-details</p>
            </div>

            {/* RIGHT COLUMN: Applicants Panel - Proper flex for desktop scroll */}
            {selectedJob.applicants > 0 && (
              <div 
                className={`${'bg-white/[0.02]'} ${
                  mobileTab === 'details' ? 'hidden lg:flex' : 'flex'
                } w-full lg:w-[400px] flex-col border-l ${'border-white/[0.08]'} overflow-hidden flex-shrink-0`}
              >
                {/* Applicants Header - Hidden on mobile (title is in tab) */}
                <div className={`hidden lg:block ${'bg-white/[0.02]'} p-6 border-b ${'border-white/[0.08]'} flex-shrink-0`}>
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-[#28C0D0]" />
                    <h3 className="text-lg font-bold text-white">
                      {selectedJob.applicants} Applicant{selectedJob.applicants !== 1 ? 's' : ''}
                    </h3>
                  </div>
                  <p className={`text-xs mt-1 ${'text-gray-400'}`}>
                    Review and hire crew for this job
                  </p>
                </div>

                {/* Applicants List - Proper scroll for desktop and mobile */}
                <div 
                  className="flex-1 p-4 lg:p-6 overflow-y-auto max-h-[calc(88dvh-200px)] lg:max-h-none"
                  style={{ 
                    WebkitOverflowScrolling: 'touch',
                    overscrollBehavior: 'contain'
                  }}
                  onWheel={(e) => {
                    // Capture scroll events on desktop
                    e.stopPropagation();
                  }}
                >
                {loadingApplicants ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#28C0D0] mx-auto"></div>
                    <p className={`mt-4 text-sm ${'text-gray-400'}`}>Loading applicants...</p>
                  </div>
                ) : jobApplicants.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className={`w-12 h-12 mx-auto mb-3 ${'text-gray-500'}`} />
                    <p className={`text-sm ${'text-gray-400'}`}>No applicants yet</p>
                  </div>
                ) : (
                  <>
                    {/* Shortlisted Applicants Section */}
                    {(() => {
                      const shortlist = selectedJob.shortlisted_users || [];
                      const shortlistedApplicants = jobApplicants.filter(app => shortlist.includes(app.user_id));
                      
                      if (shortlistedApplicants.length > 0) {
                        return (
                          <div className="mb-6">
                            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-yellow-500/30">
                              <Heart className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                              <h4 className={`text-sm font-bold ${'text-yellow-400'}`}>
                                SHORTLISTED ({shortlistedApplicants.length})
                              </h4>
                            </div>
                            <div className="space-y-3">
                              {shortlistedApplicants.map((applicant, index) => (
                                <div
                                  key={`shortlist-${applicant.application_id}`}
                                  className="p-3 rounded-lg border-2 transition-all cursor-pointer bg-yellow-900/10 border-yellow-500/50 hover:border-yellow-500 hover:bg-yellow-900/20"
                                >
                                  {/* Same compact card structure */}
                                  <div className="flex gap-3">
                                    <div className="flex-shrink-0">
                                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-500 to-amber-600 flex items-center justify-center text-white font-bold overflow-hidden ring-2 ring-yellow-400">
                                        {applicant.profile_photo_url ? (
                                          <img src={applicant.profile_photo_url} alt={applicant.full_name} className="w-full h-full object-cover" />
                                        ) : (
                                          applicant.full_name?.charAt(0) || 'U'
                                        )}
                                      </div>
                                    </div>

                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-start justify-between mb-1">
                                        <h4 className={`text-sm font-bold ${'text-white'} truncate`}>
                                          {applicant.full_name}
                                        </h4>
                                        <Badge 
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleShortlist(selectedJob.id, applicant.user_id);
                                          }}
                                          className="bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 text-[10px] px-1.5 py-0.5 cursor-pointer hover:bg-yellow-200 dark:hover:bg-yellow-900/50 transition-colors"
                                        >
                                          ⭐
                                        </Badge>
                                      </div>

                                      {/* Applied Role Badge - Show for advanced jobs */}
                                      {selectedJob.isAdvanced && (
                                        <div className="mb-1.5">
                                          {applicant.role ? (
                                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${getRoleBadgeStyle(applicant.role).className}`}>
                                              Applied as: {getRoleBadgeStyle(applicant.role).displayRole}
                                            </span>
                                          ) : (
                                            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-500/20 text-gray-400">
                                              Role not specified
                                            </span>
                                          )}
                                        </div>
                                      )}

                                      {applicant.skills && applicant.skills.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mb-2">
                                          {applicant.skills.slice(0, 2).map((skill, idx) => (
                                            <span
                                              key={idx}
                                              className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                                'bg-[rgba(40,192,208,0.12)] text-[#28C0D0] border border-[rgba(40,192,208,0.25)]'
                                              }`}
                                            >
                                              {skill}
                                            </span>
                                          ))}
                                          {applicant.skills.length > 2 && (
                                            <span className={`px-1.5 py-0.5 text-[10px] ${'text-gray-400'}`}>
                                              +{applicant.skills.length - 2}
                                            </span>
                                          )}
                                        </div>
                                      )}

                                      {applicant.cover_letter && (
                                        <p className={`text-xs ${'text-gray-400'} line-clamp-1 mb-2`}>
                                          {applicant.cover_letter}
                                        </p>
                                      )}

                                      <div className="flex gap-1.5">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => {
                                            setSelectedApplicant(applicant);
                                            fetchFullApplicantProfile(applicant.user_id);
                                          }}
                                          className="h-7 text-[11px] px-2 bg-white/5 hover:bg-white/10 text-white border border-white/10"
                                        >
                                          <Eye className="w-3 h-3 mr-1" />
                                          View
                                        </Button>
                                        {/* Show Hire button: not fully hired, not completed, AND can send offer (role-based check handles PARTIALLY_HIRED) */}
                                        {selectedJob.status !== 'HIRED' && selectedJob.status !== 'PENDING_COMPLETION' && selectedJob.status !== 'PAID_COMPLETED' &&
                                          (!selectedJob.hiredCrew || selectedJob.status === 'PARTIALLY_HIRED' || (selectedJob.isAdvanced && selectedJob.crewMembers?.some(cm => !cm.hired && !cm.cancelled))) &&
                                          canSendOfferToApplicant(selectedJob, applicant, pendingOfferRoles, pendingOfferApplicantIds) && (
                                          <Button
                                            size="sm"
                                            onClick={() => {
                                              setOfferApplicant(applicant);
                                              setShowSendOfferModal(true);
                                            }}
                                            className="h-7 text-[11px] px-2 bg-[#28C0D0] hover:bg-[#22aab8] text-[#0C1120]"
                                            style={{ boxShadow: '0 12px 28px -10px rgba(40,192,208,.6)' }}
                                          >
                                            <UserCheck className="w-3 h-3 mr-1" />
                                            Hire
                                          </Button>
                                        )}
                                        {/* Green "Offer Pending" badge for the specific applicant who received the offer */}
                                        {pendingOfferApplicantIds.has(String(applicant.user_id)) && !selectedJob.hiredCrew && (
                                          <span className="h-7 text-[11px] px-2 py-1.5 rounded-md bg-[rgba(40,192,208,0.12)] text-[#28C0D0] font-medium flex items-center">
                                            <Clock className="w-3 h-3 mr-1" />
                                            Offer Pending
                                          </span>
                                        )}
                                        {/* Gray "Offer Pending (other)" badge for OTHER applicants in the same role */}
                                        {!pendingOfferApplicantIds.has(String(applicant.user_id)) &&
                                          !canSendOfferToApplicant(selectedJob, applicant, pendingOfferRoles, pendingOfferApplicantIds) &&
                                          applicantRoleMatchesAny(normalizeRole(applicant.role), pendingOfferRoles) &&
                                          !selectedJob.hiredCrew && (
                                          <span className="h-7 text-[11px] px-2 py-1.5 rounded-md bg-white/[0.06] text-gray-400 font-medium flex items-center">
                                            <Clock className="w-3 h-3 mr-1" />
                                            Offer Pending
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {/* All Applicants Section */}
                    <div>
                      {(() => {
                        const shortlist = selectedJob.shortlisted_users || [];
                        const regularApplicants = jobApplicants.filter(app => !shortlist.includes(app.user_id));
                        
                        if (regularApplicants.length === 0) return null;
                        
                        return (
                          <>
                            {shortlist.length > 0 && (
                              <div className={`flex items-center gap-2 mb-3 pb-2 border-b ${'border-white/[0.08]'}`}>
                                <Users className="w-4 h-4 text-gray-500" />
                                <h4 className={`text-sm font-bold ${'text-gray-400'}`}>
                                  ALL APPLICANTS ({regularApplicants.length})
                                </h4>
                              </div>
                            )}
                            <div className="space-y-3">
                              {regularApplicants.map((applicant, index) => (
                                <div
                                  key={applicant.application_id}
                                  className="p-3 rounded-lg border transition-all cursor-pointer bg-white/[0.04] border-white/[0.09] hover:border-[rgba(40,192,208,0.4)] hover:bg-white/[0.06]"
                                >
                                  {/* Compact Applicant Card */}
                                  <div className="flex gap-3">
                                    {/* Profile Photo */}
                                    <div className="flex-shrink-0">
                                      <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold overflow-hidden" style={{ background: 'linear-gradient(135deg,#572B7C,#28C0D0)' }}>
                                        {applicant.profile_photo_url ? (
                                          <img src={applicant.profile_photo_url} alt={applicant.full_name} className="w-full h-full object-cover" />
                                        ) : (
                                          applicant.full_name?.charAt(0) || 'U'
                                        )}
                                      </div>
                                    </div>

                                    {/* Applicant Info */}
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-start justify-between mb-1">
                                        <h4 className={`text-sm font-bold ${'text-white'} truncate`}>
                                          {applicant.full_name}
                                        </h4>
                                        <div className="flex items-center gap-1">
                                          <Badge className="bg-[rgba(40,192,208,0.12)] text-[#28C0D0] text-[10px] px-1.5 py-0.5">
                                            #{index + 1}
                                          </Badge>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleShortlist(selectedJob.id, applicant.user_id);
                                            }}
                                            className="p-1 rounded hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors"
                                            title="Add to shortlist"
                                          >
                                            <Heart className="w-3 h-3 text-gray-400 hover:text-yellow-500" />
                                          </button>
                                        </div>
                                      </div>

                                      {/* Applied Role Badge - Show for advanced jobs */}
                                      {selectedJob.isAdvanced && (
                                        <div className="mb-1.5">
                                          {applicant.role ? (
                                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${getRoleBadgeStyle(applicant.role).className}`}>
                                              Applied as: {getRoleBadgeStyle(applicant.role).displayRole}
                                            </span>
                                          ) : (
                                            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-500/20 text-gray-400">
                                              Role not specified
                                            </span>
                                          )}
                                        </div>
                                      )}

                                      {/* Skills - compact */}
                                      {applicant.skills && applicant.skills.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mb-2">
                                          {applicant.skills.slice(0, 2).map((skill, idx) => (
                                            <span
                                              key={idx}
                                              className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                                'bg-[rgba(40,192,208,0.12)] text-[#28C0D0] border border-[rgba(40,192,208,0.25)]'
                                              }`}
                                            >
                                              {skill}
                                            </span>
                                          ))}
                                          {applicant.skills.length > 2 && (
                                            <span className={`px-1.5 py-0.5 text-[10px] ${'text-gray-400'}`}>
                                              +{applicant.skills.length - 2}
                                            </span>
                                          )}
                                        </div>
                                      )}

                                      {/* Cover Letter Preview - 1 line */}
                                      {applicant.cover_letter && (
                                        <p className={`text-xs ${'text-gray-400'} line-clamp-1 mb-2`}>
                                          {applicant.cover_letter}
                                        </p>
                                      )}

                                      {/* Action Buttons - Compact */}
                                      <div className="flex gap-1.5">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => {
                                            setSelectedApplicant(applicant);
                                            fetchFullApplicantProfile(applicant.user_id);
                                          }}
                                          className="h-7 text-[11px] px-2 bg-white/5 hover:bg-white/10 text-white border border-white/10"
                                        >
                                          <Eye className="w-3 h-3 mr-1" />
                                          View
                                        </Button>
                                        {/* Show Hire button: not fully hired, not completed, AND can send offer (role-based check handles PARTIALLY_HIRED) */}
                                        {selectedJob.status !== 'HIRED' && selectedJob.status !== 'PENDING_COMPLETION' && selectedJob.status !== 'PAID_COMPLETED' &&
                                          (!selectedJob.hiredCrew || selectedJob.status === 'PARTIALLY_HIRED' || (selectedJob.isAdvanced && selectedJob.crewMembers?.some(cm => !cm.hired && !cm.cancelled))) &&
                                          canSendOfferToApplicant(selectedJob, applicant, pendingOfferRoles, pendingOfferApplicantIds) && (
                                          <Button
                                            size="sm"
                                            onClick={() => {
                                              setOfferApplicant(applicant);
                                              setShowSendOfferModal(true);
                                            }}
                                            className="h-7 text-[11px] px-2 bg-[#28C0D0] hover:bg-[#22aab8] text-[#0C1120]"
                                            style={{ boxShadow: '0 12px 28px -10px rgba(40,192,208,.6)' }}
                                          >
                                            <UserCheck className="w-3 h-3 mr-1" />
                                            Hire
                                          </Button>
                                        )}
                                        {/* Green "Offer Pending" badge for the specific applicant who received the offer */}
                                        {pendingOfferApplicantIds.has(String(applicant.user_id)) && !selectedJob.hiredCrew && (
                                          <span className="h-7 text-[11px] px-2 py-1.5 rounded-md bg-[rgba(40,192,208,0.12)] text-[#28C0D0] font-medium flex items-center">
                                            <Clock className="w-3 h-3 mr-1" />
                                            Offer Pending
                                          </span>
                                        )}
                                        {/* Gray "Offer Pending (other)" badge for OTHER applicants in the same role */}
                                        {!pendingOfferApplicantIds.has(String(applicant.user_id)) &&
                                          !canSendOfferToApplicant(selectedJob, applicant, pendingOfferRoles, pendingOfferApplicantIds) &&
                                          applicantRoleMatchesAny(normalizeRole(applicant.role), pendingOfferRoles) &&
                                          !selectedJob.hiredCrew && (
                                          <span className="h-7 text-[11px] px-2 py-1.5 rounded-md bg-white/[0.06] text-gray-400 font-medium flex items-center">
                                            <Clock className="w-3 h-3 mr-1" />
                                            Offer Pending
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </>
                )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )}

      {/* Applicant Profile Modal — Modern Redesign */}
      {selectedApplicant && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[80] flex items-end sm:items-center justify-center"
          onClick={() => setSelectedApplicant(null)}
        >
          <div
            className="w-full sm:max-w-2xl lg:max-w-4xl max-h-[90dvh] sm:max-h-[88dvh] rounded-t-3xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden sm:mx-4"
            onClick={(e) => e.stopPropagation()}
            style={{ touchAction: 'pan-y', background: '#12162A', border: '1px solid rgba(255,255,255,.12)' }}
          >
            {/* Header — Command Deck violet→magenta band */}
            <div className="flex-shrink-0 px-5 lg:px-6 pt-5 pb-4" style={{ background: 'linear-gradient(120deg,#572B7C,#78287C)', borderBottom: '1px solid rgba(255,255,255,.12)' }}>
              {/* Mobile drag indicator */}
              <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-700 mx-auto mb-4 sm:hidden" />

              <div className="flex items-start gap-4">
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-2xl overflow-hidden ring-2 ring-[rgba(40,192,208,0.35)] shadow-lg">
                    {selectedApplicant.profile_photo_url ? (
                      <img src={selectedApplicant.profile_photo_url} alt={selectedApplicant.full_name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white font-bold text-2xl" style={{ background: 'linear-gradient(135deg,#572B7C,#28C0D0)' }}>
                        {selectedApplicant.full_name?.charAt(0) || 'U'}
                      </div>
                    )}
                  </div>
                  {fullApplicantProfile?.reviewStats?.average_rating > 0 && (
                    <div className="absolute -bottom-1.5 -right-1.5 bg-yellow-500 text-black text-[10px] font-black px-1.5 py-0.5 rounded-lg flex items-center gap-0.5 shadow-md">
                      <Star className="w-2.5 h-2.5 fill-current" />
                      {fullApplicantProfile.reviewStats.average_rating.toFixed(1)}
                    </div>
                  )}
                </div>

                {/* Name + Meta */}
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg lg:text-xl font-bold truncate text-white" style={{ fontFamily: 'Space Grotesk' }}>
                    {selectedApplicant.full_name}
                  </h2>
                  {(() => {
                    const profile = fullApplicantProfile || selectedApplicant;
                    const skills = profile.skills || [];
                    const roleLabels = { 'photographer': 'Photographer', 'videographer': 'Videographer', 'editor': 'Editor', 'editor_photos': 'Photo Editor', 'editor_video': 'Video Editor', 'assistant': 'Assistant', 'drone_operator': 'Drone Operator', 'voxpop_videographer': 'VoxPop Videographer' };
                    const roles = skills.slice(0, 4).map(s => roleLabels[s] || s);
                    return roles.length > 0 && (
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        {roles.map((role, idx) => (
                          <span key={idx} className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md bg-[rgba(40,192,208,0.12)] text-[#28C0D0] border border-[rgba(40,192,208,0.25)]">
                            {role}
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                  <div className="flex items-center gap-3 mt-1.5">
                    {fullApplicantProfile?.reviewStats?.total_reviews > 0 && (
                      <span className={`text-xs ${'text-gray-400'}`}>
                        {fullApplicantProfile.reviewStats.total_reviews} review{fullApplicantProfile.reviewStats.total_reviews !== 1 ? 's' : ''}
                      </span>
                    )}
                    {(() => {
                      const profile = fullApplicantProfile || selectedApplicant;
                      const city = profile.location_city || profile.city;
                      const state = profile.location_state || profile.state;
                      const locationStr = city && state ? `${city}, ${state}` : city ? city : typeof profile.location === 'string' ? profile.location : null;
                      return locationStr && (
                        <span className={`inline-flex items-center gap-1 text-xs ${'text-gray-400'}`}>
                          <MapPin className="w-3 h-3" />
                          {locationStr}
                        </span>
                      );
                    })()}
                  </div>
                </div>

                {/* Header Actions */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleShortlist(selectedJob.id, selectedApplicant.user_id); }}
                    className={`p-2 rounded-xl transition-all ${
                      (selectedJob.shortlisted_users || []).includes(selectedApplicant.user_id)
                        ? 'bg-yellow-500/15 text-yellow-500'
                        : 'text-gray-500 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <Heart className={`w-4 h-4 ${(selectedJob.shortlisted_users || []).includes(selectedApplicant.user_id) ? 'fill-current' : ''}`} />
                  </button>
                  <button
                    onClick={() => setSelectedApplicant(null)}
                    className={`p-2 rounded-xl transition-all ${'text-gray-500 hover:text-white hover:bg-white/5'}`}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Action Buttons Row */}
              <div className="flex items-center gap-2 mt-4">
                <Button
                  onClick={async () => {
                    try {
                      const currentUser = JSON.parse(localStorage.getItem('shootzu_user') || '{}');
                      const response = await axios.post(
                        `${process.env.REACT_APP_BACKEND_URL}/api/messages/conversation/create`,
                        {
                          job_id: selectedJob.id,
                          job_title: selectedJob.title,
                          user1_email: currentUser.email,
                          user2_email: selectedApplicant.email || selectedApplicant.applicant_email
                        }
                      );
                      const convId = response.data.conversation?.id || response.data.conversation_id;
                      setSelectedApplicant(null);
                      navigate("/messages", { state: { conversationId: convId, autoOpen: true } });
                      toast.success(response.data.created ? 'New conversation started!' : 'Opening conversation...');
                    } catch (error) {
                      console.error('Error creating conversation:', error);
                      toast.error('Failed to start conversation');
                    }
                  }}
                  size="sm"
                  className="h-9 px-4 text-xs font-bold rounded-xl flex-1 sm:flex-none bg-white/5 hover:bg-white/10 text-white border border-white/10"
                  variant="outline"
                >
                  <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
                  Message
                </Button>
                {(!selectedJob.hiredCrew || selectedJob.status === 'PARTIALLY_HIRED' || (selectedJob.isAdvanced && selectedJob.crewMembers?.some(cm => !cm.hired && !cm.cancelled))) && canSendOfferToApplicant(selectedJob, selectedApplicant, pendingOfferRoles, pendingOfferApplicantIds) && (
                  <Button
                    onClick={() => {
                      setOfferApplicant(selectedApplicant);
                      setShowSendOfferModal(true);
                      setSelectedApplicant(null);
                    }}
                    size="sm"
                    className="h-9 px-4 text-xs font-bold rounded-xl bg-[#28C0D0] hover:bg-[#22aab8] text-[#0C1120] flex-1 sm:flex-none"
                    style={{ boxShadow: '0 12px 28px -10px rgba(40,192,208,.6)' }}
                  >
                    <UserCheck className="w-3.5 h-3.5 mr-1.5" />
                    Send Offer
                  </Button>
                )}
                {!canSendOfferToApplicant(selectedJob, selectedApplicant, pendingOfferRoles, pendingOfferApplicantIds) &&
                  (!selectedJob.hiredCrew || selectedJob.status === 'PARTIALLY_HIRED' || (selectedJob.isAdvanced && selectedJob.crewMembers?.some(cm => !cm.hired && !cm.cancelled))) && (
                  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl ${
                    pendingOfferApplicantIds.has(String(selectedApplicant.user_id))
                      ? 'bg-[rgba(40,192,208,0.12)] text-[#28C0D0]'
                      : 'bg-white/[0.06] text-gray-400'
                  }`}>
                    <Clock className="w-3.5 h-3.5" />
                    Offer Pending{selectedJob.isAdvanced && selectedApplicant?.role ? ` (${selectedApplicant.role})` : ''}
                  </span>
                )}
              </div>
            </div>

            {/* Scrollable Content */}
            <div
              className="flex-1 overflow-y-auto"
              style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}
            >
              {loadingFullProfile ? (
                <div className="text-center py-20">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-transparent border-t-[#28C0D0] mx-auto"></div>
                  <p className={`mt-3 text-sm ${'text-gray-500'}`}>Loading profile...</p>
                </div>
              ) : (
                <div className="p-5 lg:p-6">
                  {/* Two-column layout on desktop */}
                  <div className="lg:grid lg:grid-cols-5 lg:gap-6 space-y-4 lg:space-y-0">

                    {/* Left Column — Main content (3/5) */}
                    <div className="lg:col-span-3 space-y-4">

                      {/* Application Message */}
                      <div className={`rounded-xl overflow-hidden ${'bg-[rgba(255,255,255,0.045)] border border-white/[0.09]'}`}>
                        <div className="px-4 py-2.5 flex items-center gap-2 bg-white/[0.03] border-b border-white/[0.06]">
                          <MessageSquare className="w-3.5 h-3.5 text-[#28C0D0]" />
                          <h3 className="text-xs font-bold uppercase tracking-wider text-[#28C0D0]">Application Message</h3>
                        </div>
                        <div className="px-4 py-3.5">
                          {selectedApplicant.cover_letter ? (
                            <p className={`text-sm leading-relaxed whitespace-pre-wrap ${'text-gray-300'}`}>
                              {selectedApplicant.cover_letter}
                            </p>
                          ) : (
                            <p className={`text-sm italic ${'text-gray-500'}`}>No message provided</p>
                          )}
                        </div>
                      </div>

                      {/* About / Bio */}
                      {(fullApplicantProfile?.bio || selectedApplicant.bio) && (
                        <div className={`rounded-xl p-4 ${'bg-[rgba(255,255,255,0.045)] border border-white/[0.09]'}`}>
                          <h3 className={`text-xs font-bold uppercase tracking-wider mb-2.5 ${'text-gray-500'}`}>About</h3>
                          <p className={`text-sm leading-relaxed whitespace-pre-wrap ${'text-gray-300'}`}>
                            {fullApplicantProfile?.bio || selectedApplicant.bio}
                          </p>
                        </div>
                      )}

                      {/* Portfolio Gallery */}
                      {(() => {
                        const profile = fullApplicantProfile || selectedApplicant;
                        const portfolioImages = profile.portfolio_images || [];
                        return portfolioImages.length > 0 && (
                          <div className={`rounded-xl p-4 ${'bg-[rgba(255,255,255,0.045)] border border-white/[0.09]'}`}>
                            <h3 className={`text-xs font-bold uppercase tracking-wider mb-3 ${'text-gray-500'}`}>
                              Portfolio
                              <span className={`ml-1.5 font-normal ${'text-gray-500'}`}>({portfolioImages.length})</span>
                            </h3>
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                              {portfolioImages.map((imageUrl, idx) => (
                                <div
                                  key={idx}
                                  className="aspect-square rounded-lg overflow-hidden bg-white/[0.05] cursor-pointer group relative hover:ring-2 hover:ring-[rgba(40,192,208,0.4)] transition-all"
                                  onClick={() => {
                                    setLightboxImages(portfolioImages);
                                    setLightboxImage(imageUrl);
                                    setLightboxIndex(idx);
                                  }}
                                >
                                  <img
                                    src={imageUrl}
                                    alt={`Portfolio ${idx + 1}`}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 pointer-events-none"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.parentElement.innerHTML = '<div class="flex items-center justify-center h-full text-gray-500 text-xs">Unavailable</div>';
                                    }}
                                  />
                                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-1.5">
                                      <Eye className="w-3.5 h-3.5 text-gray-900" />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Reviews */}
                      {fullApplicantProfile?.reviews && fullApplicantProfile.reviews.length > 0 && (
                        <div className={`rounded-xl p-4 ${'bg-[rgba(255,255,255,0.045)] border border-white/[0.09]'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <h3 className={`text-xs font-bold uppercase tracking-wider ${'text-gray-500'}`}>Reviews</h3>
                            {fullApplicantProfile.reviewStats && (
                              <div className="flex items-center gap-1.5">
                                <div className="flex items-center gap-0.5">
                                  {[...Array(5)].map((_, i) => (
                                    <Star key={i} className={`w-3 h-3 ${i < Math.round(fullApplicantProfile.reviewStats.average_rating || 0) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-700'}`} />
                                  ))}
                                </div>
                                <span className={`text-xs font-semibold ${'text-gray-400'}`}>
                                  {fullApplicantProfile.reviewStats.average_rating?.toFixed(1)}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                            {fullApplicantProfile.reviews.slice(0, 10).map((review, idx) => (
                              <div key={idx} className="p-3 rounded-lg bg-white/[0.02]">
                                <div className="flex items-center justify-between mb-1">
                                  <span className={`text-xs font-semibold ${'text-white'}`}>
                                    {review.reviewer_name || 'Anonymous'}
                                  </span>
                                  <div className="flex items-center gap-0.5">
                                    {[...Array(5)].map((_, i) => (
                                      <Star key={i} className={`w-2.5 h-2.5 ${i < (review.rating || 0) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-700'}`} />
                                    ))}
                                  </div>
                                </div>
                                {(review.review_text || review.comment) && (
                                  <p className={`text-xs leading-relaxed ${'text-gray-400'}`}>
                                    {review.review_text || review.comment}
                                  </p>
                                )}
                              </div>
                            ))}
                            {fullApplicantProfile.reviews.length > 10 && (
                              <p className={`text-center text-xs py-1 ${'text-gray-500'}`}>
                                + {fullApplicantProfile.reviews.length - 10} more reviews
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {fullApplicantProfile && (!fullApplicantProfile.reviews || fullApplicantProfile.reviews.length === 0) && (
                        <div className={`rounded-xl p-6 text-center ${'bg-[rgba(255,255,255,0.045)] border border-white/[0.09]'}`}>
                          <Star className="w-5 h-5 mx-auto mb-1.5 text-gray-700" />
                          <p className={`text-xs ${'text-gray-500'}`}>No reviews yet</p>
                        </div>
                      )}
                    </div>

                    {/* Right Column — Sidebar info (2/5) */}
                    <div className="lg:col-span-2 space-y-4">

                      {/* Contact Details - Only shown after hire */}
                      {selectedJob.status === 'hired' && fullApplicantProfile && (
                        <div className="rounded-xl p-4 bg-[rgba(40,192,208,0.06)] border border-[rgba(40,192,208,0.2)]">
                          <h3 className="text-xs font-bold uppercase tracking-wider mb-2.5 flex items-center gap-1.5 text-[#28C0D0]">
                            <Lock className="w-3 h-3" /> Contact Unlocked
                          </h3>
                          <div className="space-y-2">
                            {fullApplicantProfile.email && (
                              <a href={`mailto:${fullApplicantProfile.email}`} className={`flex items-center gap-2 text-xs ${'text-gray-300 hover:text-white'} transition-colors`}>
                                <Mail className="w-3.5 h-3.5 flex-shrink-0" /> <span className="truncate">{fullApplicantProfile.email}</span>
                              </a>
                            )}
                            {fullApplicantProfile.phone && (
                              <a href={`tel:${fullApplicantProfile.phone}`} className={`flex items-center gap-2 text-xs ${'text-gray-300 hover:text-white'} transition-colors`}>
                                <Phone className="w-3.5 h-3.5 flex-shrink-0" /> {fullApplicantProfile.phone}
                              </a>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Skills */}
                      {(fullApplicantProfile?.skills?.length > 0 || selectedApplicant.skills?.length > 0) && (
                        <div className={`rounded-xl p-4 ${'bg-[rgba(255,255,255,0.045)] border border-white/[0.09]'}`}>
                          <h3 className={`text-xs font-bold uppercase tracking-wider mb-2.5 ${'text-gray-500'}`}>Skills</h3>
                          <div className="flex flex-wrap gap-1.5">
                            {(fullApplicantProfile?.skills || selectedApplicant.skills).map((skill, idx) => (
                              <span key={idx} className={`px-2.5 py-1 rounded-lg text-xs font-medium ${'bg-[rgba(40,192,208,0.12)] text-[#28C0D0] border border-[rgba(40,192,208,0.25)]'}`}>
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Equipment */}
                      {fullApplicantProfile?.equipment?.length > 0 && (
                        <div className={`rounded-xl p-4 ${'bg-[rgba(255,255,255,0.045)] border border-white/[0.09]'}`}>
                          <h3 className={`text-xs font-bold uppercase tracking-wider mb-2.5 ${'text-gray-500'}`}>Equipment</h3>
                          <div className="flex flex-wrap gap-1.5">
                            {fullApplicantProfile.equipment.map((item, idx) => (
                              <span key={idx} className="px-2.5 py-1 rounded-lg text-xs font-medium bg-white/[0.04] text-gray-300 border border-white/[0.06]">
                                {item}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Certifications */}
                      {fullApplicantProfile?.certifications?.length > 0 && (
                        <div className={`rounded-xl p-4 ${'bg-[rgba(255,255,255,0.045)] border border-white/[0.09]'}`}>
                          <h3 className={`text-xs font-bold uppercase tracking-wider mb-2.5 ${'text-gray-500'}`}>Certifications</h3>
                          <div className="flex flex-wrap gap-1.5">
                            {fullApplicantProfile.certifications.map((cert, idx) => (
                              <span key={idx} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium ${'bg-[rgba(40,192,208,0.12)] text-[#28C0D0] border border-[rgba(40,192,208,0.25)]'}`}>
                                <CheckCircle className="w-3 h-3" /> {cert}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Links */}
                      {(fullApplicantProfile?.website || fullApplicantProfile?.social_links) && (
                        <div className={`rounded-xl p-4 ${'bg-[rgba(255,255,255,0.045)] border border-white/[0.09]'}`}>
                          <h3 className={`text-xs font-bold uppercase tracking-wider mb-2.5 ${'text-gray-500'}`}>Links</h3>
                          <div className="space-y-2">
                            {fullApplicantProfile?.website && (
                              <a href={fullApplicantProfile.website.startsWith('http') ? fullApplicantProfile.website : `https://${fullApplicantProfile.website}`}
                                target="_blank" rel="noopener noreferrer"
                                className={`flex items-center gap-2 text-xs ${'text-[#28C0D0] hover:text-[#5fd3e0]'} transition-colors`}>
                                <Globe className="w-3.5 h-3.5 flex-shrink-0" />
                                <span className="truncate">{fullApplicantProfile.website.replace(/^https?:\/\//, '')}</span>
                                <ExternalLink className="w-2.5 h-2.5 flex-shrink-0 opacity-50" />
                              </a>
                            )}
                            {fullApplicantProfile?.social_links && Object.entries(fullApplicantProfile.social_links).map(([platform, url]) => url && (
                              <a key={platform} href={url} target="_blank" rel="noopener noreferrer"
                                className={`flex items-center gap-2 text-xs capitalize ${'text-[#28C0D0] hover:text-[#5fd3e0]'} transition-colors`}>
                                <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" /> {platform}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sticky Footer — Send Offer */}
            {!loadingFullProfile && selectedJob.status !== 'HIRED' && selectedJob.status !== 'PENDING_COMPLETION' && selectedJob.status !== 'PAID_COMPLETED' &&
              (!selectedJob.hiredCrew || selectedJob.status === 'PARTIALLY_HIRED' || (selectedJob.isAdvanced && selectedJob.crewMembers?.some(cm => !cm.hired && !cm.cancelled))) &&
              canSendOfferToApplicant(selectedJob, selectedApplicant, pendingOfferRoles, pendingOfferApplicantIds) && (
              <div className="flex-shrink-0 px-5 py-3 border-t border-white/[0.08] bg-[#12162A]/95 backdrop-blur-sm">
                <Button
                  onClick={() => {
                    setOfferApplicant(selectedApplicant);
                    setShowSendOfferModal(true);
                    setSelectedApplicant(null);
                  }}
                  className="w-full h-11 text-sm font-bold bg-[#28C0D0] hover:bg-[#22aab8] text-[#0C1120] rounded-xl"
                  style={{ boxShadow: '0 12px 28px -10px rgba(40,192,208,.6)' }}
                >
                  <UserCheck className="w-4 h-4 mr-2" />
                  Send Offer to Hire {selectedApplicant.full_name?.split(' ')[0] || 'Crew'}
                </Button>
              </div>
            )}
            <p className="text-[8px] text-center py-1 opacity-20 select-none">modal:applicant-profile</p>
          </div>
        </div>
      )}

      {/* DEPRECATED: Conditional Job Details Modal - Using inline modal instead */}
      {false && showJobDetails && selectedJob && (
        selectedJob.isAdvanced ? (
          <AdvancedJobDetailsModal
            job={selectedJob}
            isOpen={showJobDetails}
            onClose={() => setShowJobDetails(false)}
            onEdit={(job) => {
              setShowJobDetails(false);
              setEditingJob(job);
              setShowEditJobModal(true);
            }}
            onCancel={handleCancelJob}
          />
        ) : (
          <JobDetailsModal
            job={selectedJob}
            isOpen={showJobDetails}
            onClose={() => setShowJobDetails(false)}
          />
        )
      )}

      {/* Edit Job Modal - Conditional */}
      {showEditJobModal && editingJob && (
        editingJob.isAdvanced ? (
          <AdvancedEditJobModal
            job={editingJob}
            isOpen={showEditJobModal}
            onClose={() => {
              setShowEditJobModal(false);
              setEditingJob(null);
            }}
            onUpdate={() => {
              fetchPostedJobs();
              setShowEditJobModal(false);
              setEditingJob(null);
              toast.success('Advanced job updated successfully');
            }}
            isDark={isDark}
            ownerMode
          />
        ) : (
          <EditJobModal
            job={editingJob}
            isOpen={showEditJobModal}
            onClose={() => {
              setShowEditJobModal(false);
              setEditingJob(null);
            }}
            onUpdate={() => {
              fetchPostedJobs();
              setShowEditJobModal(false);
              setEditingJob(null);
              toast.success('Job updated successfully');
            }}
          />
        )
      )}

      {/* Send Offer Modal */}
      {showSendOfferModal && offerApplicant && selectedJob && (
        <SendOfferModal
          isOpen={showSendOfferModal}
          onClose={() => {
            setShowSendOfferModal(false);
            setOfferApplicant(null);
          }}
          job={selectedJob}
          applicant={offerApplicant}
          isDark={isDark}
          onOfferSent={(data) => {
            // Refresh job list to update status
            fetchPostedJobs();
            // Refresh offers list so the new pending offer immediately blocks
            // duplicate offers for the same role and shows the pending badge
            if (selectedJob?.id) {
              fetchJobOffers(selectedJob.id);
            }
            // Also update the selected job if viewing it
            if (selectedJob) {
              setSelectedJob(prev => ({
                ...prev,
                status: prev.isAdvanced ? 'PARTIALLY_HIRED' : 'OFFER_SENT',
                offer_sent: true,
                pending_offer: {
                  applicant_id: offerApplicant?.user_id || offerApplicant?.applicant_id,
                  applicant_name: offerApplicant?.full_name || offerApplicant?.applicant_name,
                  applicant_email: offerApplicant?.email || offerApplicant?.applicant_email,
                  role: offerApplicant?.role || data.role || null,
                  sent_at: new Date().toISOString(),
                  expires_at: data.offer_expires_at
                }
              }));
            }
          }}
        />
      )}

      {/* Complete Job Modal */}
      {showCompleteJobModal && selectedJob && (
        <CompleteJobModal
          isOpen={showCompleteJobModal}
          onClose={() => setShowCompleteJobModal(false)}
          job={selectedJob}
          hiredCrew={selectedJob.hiredCrew}
          isDark={isDark}
          onJobCompleted={() => {
            fetchPostedJobs();
            setShowCompleteJobModal(false);
          }}
        />
      )}
      {/* Review Modal for Release Payment */}
      {showReviewModal && selectedJob && (
        <ReviewModal
          isOpen={showReviewModal}
          onClose={() => {
            setShowReviewModal(false);
            setReviewingCrewMember(null);
          }}
          job={selectedJob}
          crewMember={reviewingCrewMember}
          isDark={isDark}
          onComplete={() => {
            fetchPostedJobs();
            setShowReviewModal(false);
            setReviewingCrewMember(null);
            setSelectedJob(null);
          }}
        />
      )}
      {/* Cancel Crew Member Modal */}
      <CancelCrewMemberModal
        isOpen={showCancelCrewMemberModal}
        job={selectedJob}
        crewMember={cancellingCrewMember}
        crewMemberIndex={cancellingCrewMemberIndex}
        userEmail={user.email}
        onClose={() => {
          setShowCancelCrewMemberModal(false);
          setCancellingCrewMember(null);
          setCancellingCrewMemberIndex(null);
        }}
        onSuccess={() => {
          fetchPostedJobs();
          setShowCancelCrewMemberModal(false);
          setCancellingCrewMember(null);
          setCancellingCrewMemberIndex(null);
          // Refresh selected job to show updated crew
          setSelectedJob(null);
          setShowJobDetails(false);
        }}
      />

      {/* Cancel Unhired Position Modal */}
      <CancelUnhiredPositionModal
        isOpen={showCancelPositionModal}
        job={selectedJob}
        position={cancellingPosition}
        crewMemberIndex={cancellingPositionIndex}
        userEmail={user.email}
        onClose={() => {
          setShowCancelPositionModal(false);
          setCancellingPosition(null);
          setCancellingPositionIndex(null);
        }}
        onSuccess={() => {
          fetchPostedJobs();
          setShowCancelPositionModal(false);
          setCancellingPosition(null);
          setCancellingPositionIndex(null);
          setSelectedJob(null);
          setShowJobDetails(false);
        }}
      />

      {/* Profile Blocker Modal */}
      <ProfileBlockerModal
        isOpen={showProfileBlocker}
        onClose={() => setShowProfileBlocker(false)}
        userStatus={userStatus}
        userName={user.full_name || user.first_name || 'User'}
        userId={user.id}
      />

      {/* Cancellation Payment Blocker Modal */}
      <CancellationPaymentBlockerModal
        isOpen={showCancellationBlocker}
        outstandingFees={outstandingCancellationFees}
      />

      {/* Portfolio Lightbox Modal */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-sm"
          onClick={() => {
            setLightboxImage(null);
            setLightboxIndex(null);
            setLightboxImages([]);
          }}
        >
          {/* Close button */}
          <button
            onClick={() => {
              setLightboxImage(null);
              setLightboxIndex(null);
              setLightboxImages([]);
            }}
            className="absolute top-4 right-4 z-10 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Image counter */}
          {lightboxImages.length > 1 && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 px-4 py-2 rounded-full bg-black/50 text-white text-sm font-medium">
              {lightboxIndex + 1} / {lightboxImages.length}
            </div>
          )}

          {/* Previous button */}
          {lightboxImages.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const newIndex = lightboxIndex === 0 ? lightboxImages.length - 1 : lightboxIndex - 1;
                setLightboxIndex(newIndex);
                setLightboxImage(lightboxImages[newIndex]);
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
          )}

          {/* Image */}
          <img
            src={lightboxImage}
            alt="Portfolio"
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Next button */}
          {lightboxImages.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const newIndex = lightboxIndex === lightboxImages.length - 1 ? 0 : lightboxIndex + 1;
                setLightboxIndex(newIndex);
                setLightboxImage(lightboxImages[newIndex]);
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default HireCrewRedesigned;
